/**
 * `<ProgressNoteEdit>` — Phase 3 (LD-6, LD-18, LD-19).
 *
 * RHF-driven single-form Progress Note. Replaces the legacy 4-textarea SOAP
 * form. The form state mirrors the AI tool_use shape directly (summary +
 * structured arrays + billing suggestion); the FE adapter at `draftToSoap.ts`
 * translates form state → `SoapNoteDto` at submit time so the existing
 * `/visits/:encounterId/soap-note` cascade does the FHIR writes.
 *
 * Submit path:
 *   form state
 *     → `draftToSoap(formState, existing?)`
 *     → POST /visits/:encounterId/soap-note  (middleware client)
 *     → `VisitService.evaluateSoapNote()`  (visit.service.ts:133)
 *         → QuestionnaireResponse + Task + Patient resources + DocumentReference
 *           + Provenance + Encounter update
 *
 * Phases 4 + 5 will mount `<NoteStatsStrip>` above the TOC and
 * `<BillingSection>` below `<PlanSection>` (their slot markers are
 * preserved as TODO comments).
 */
import {
  Button,
  Group,
  Loader,
  LoadingOverlay,
  Menu,
  Paper,
  Portal as MantinePortal,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useDebouncedCallback } from '@mantine/hooks'
import { useMedplum } from '@medplum/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm, FormProvider, useFieldArray, useFormState, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { normalizeErrorString } from '@medplum/core'
import {
  IconCheck,
  IconEdit,
  IconFileText,
  IconMicrophone,
  IconRefresh,
  IconSparkles,
} from '@tabler/icons-react'
import { useAiDictate } from './dictation/useAiDictate'
import { AiDictateBar } from './dictation/AiDictateBar'
import { AiDictateContext } from './dictation/AiDictateContext'
import { serializeNoteForMerge } from './dictation/serializeNoteForMerge'
import type {
  AllergyIntolerance,
  Condition,
  DocumentReference,
  Encounter,
  FamilyMemberHistory,
  Provenance,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes'
import { useClient, useOrpc, type OrpcClient } from '@/hooks/useClient'
import { useSaveProgressNoteDraft } from '@/pages/Patient/hooks/useSaveProgressNoteDraft'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { logError } from '@/utils/errorUtils'
import { formatDateTime, toClinicDateTimeLocal } from '@/utils'
import {
  useScribeFormBridge,
  type StructuredScribeDraft,
} from '@/pages/Call/context/ScribeFormBridgeContext'
import { useCallSession } from '@/components/session/callSessionContext'
import { useEncounterScribeState } from '@/pages/Call/hooks/useEncounterScribeState'
import { useFinalizeScribe, type FinalizeScribeResult } from '@/pages/Call/hooks/useFinalizeScribe'
import { loadStoredScribeDraft } from '@/pages/Call/hooks/storedScribeDraft'
import { toStructuredDraft, confirmDraftDisclaimer } from '@/pages/Call/hooks/applyDraftAndReveal'
import { ScribeDraftGenerationError } from '@/pages/Call/hooks/scribeErrors'
import {
  readObservationsFromStorage,
  clearObservationsFromStorage,
} from '@/components/scribe/observationsStorage'
import { confirmLoadAiDraft, confirmRegenerateNote } from '@/pages/Call/hooks/confirmAiDraftActions'
import { GeneratingProgress } from '@/components/scribe/EndSessionPrompt'
import { isEffectivelyBlankNote } from './isEffectivelyBlankNote'
import {
  getSelectedCandidate,
  makeEmptyProgressNoteFormValues,
  progressNoteEditFormSchema,
  type ObservationItem,
  type ProgressNoteEditFormValues,
} from './progressNoteEditSchema'
import { applyNoteDiagnosisChange, type NoteDiagnosisChange } from './billingDxSync'
import { ICD10_SYSTEM } from './fhirSystems'
import {
  isSignedNoteConflict,
  SIGNED_NOTE_CONFLICT_MESSAGE,
  SIGNED_NOTE_CONFLICT_TITLE,
} from './signedNoteConflict'

/**
 * Build a stable dedupe key for clinical resources that carry forward into
 * the form. Prefer the FHIR code (deterministic across visits); fall back to
 * the display string (case/whitespace normalized) for resources where the
 * coding wasn't captured. Returns `null` when neither is available — the
 * caller treats `null` keys as never-equal so the entries pass through.
 */
function clinicalDedupeKey(
  item: { code?: string | null; display?: string | null } | undefined,
): string | null {
  if (!item) return null
  const code = item.code?.trim()
  if (code) return `c:${code}`
  const display = item.display?.trim().toLowerCase()
  if (display) return `d:${display}`
  return null
}

/**
 * Merge a structured AI scribe draft into a base set of form values, returning
 * a complete `ProgressNoteEditFormValues` snapshot suitable for
 * `methods.reset(...)`. Scalar form-only fields (`noteDate`, `visitType`,
 * `encounterDetails`, `billingEntries`, `followUps`) carry through from
 * `base`. The AI structured arrays MERGE on top of the base's carry-forward
 * arrays (FE-3): AI items win on duplicates (by code, falling back to
 * display); carry-forward items the AI didn't touch survive. Each AI array
 * entry gets a fresh `crypto.randomUUID()` id for `useFieldArray` identity
 * tracking.
 *
 * Used in two places:
 *  1. The hydrate effect's create-mode branch, when the bridge has a pending
 *     draft cached from a `populateFromAi(draft)` that fired before this
 *     form mounted. Applying the draft atomically via `methods.reset(merged)`
 *     in the same synchronous tick as the hydrate avoids the
 *     queueMicrotask-vs-reset race that lost the AI values under React 19's
 *     concurrent scheduler.
 *  2. The `registerPopulateFromAi` callback, when a draft arrives mid-edit
 *     (the scribe is finalized while the form is already mounted). Using
 *     `methods.reset(merged)` here too keeps both paths consistent and means
 *     the AI populate always wins regardless of effect ordering.
 */
function applyAiDraftToFormValues(
  base: ProgressNoteEditFormValues,
  draft: StructuredScribeDraft,
): { values: ProgressNoteEditFormValues; billingSync: AiBillingSync } {
  // Merge AI items ON TOP of the form's current values, don't replace. The
  // create-mode hydrate branch seeds `base.allergies / conditions` from the
  // patient's active chart (safety-context carry-forward). `base.medications`
  // and `base.observations` are deliberately NOT chart-seeded — carry-forward
  // is disabled for meds + vitals so those sections reflect ONLY this visit.
  // When the AI draft arrives, AI items win on duplicates (matched by code,
  // falling back to display) while any non-AI items already in the form
  // (allergy/problem carry-forward, or the doctor's manual edits) survive.
  //
  // The merge is uniform across all four arrays; with meds/vitals starting
  // empty, the result for those two is simply the AI-captured meds and the
  // this-visit vitals (no prior reading is back-filled).

  // --- Allergies (PD-1141): update-in-place. An AI row that targets a known
  //     chart allergy — by the echoed `chartId`, falling back to the dedupe
  //     key for a missing/hallucinated id — UPDATES the external base row
  //     (reaction / status / severity + the "Updated" badge) instead of being
  //     dropped. The row stays external (chart-authoritative identity, skipped
  //     at Lock; the change lives in the note only). Non-matching AI rows
  //     append as new editable rows; AI still wins over colliding non-external
  //     base rows (today's dedupe, unchanged). External rows always survive. ---
  type AllergyRow = ProgressNoteEditFormValues['allergies'][number]
  const externalById = new Map<string, AllergyRow>()
  const externalByKey = new Map<string, AllergyRow>()
  for (const row of base.allergies) {
    if (!row.external) continue
    if (row.chartId) externalById.set(row.chartId, row)
    const key = clinicalDedupeKey(row)
    if (key && !externalByKey.has(key)) externalByKey.set(key, row)
  }
  const updatesByRowId = new Map<string, StructuredScribeDraft['allergies'][number]>()
  const newAiRows: AllergyRow[] = []
  for (const ai of draft.allergies) {
    const key = clinicalDedupeKey(ai)
    const byId = ai.chartId ? externalById.get(ai.chartId) : undefined
    // Distrust an id echo that CONTRADICTS the row's coded identity (same code
    // system, different code): a wrong-row id carrying the right chart code
    // would silently patch another allergen. Absent/differing systems keep
    // trusting the id — the echo exists to bridge naming/coding gaps.
    const idContradictsCode = Boolean(
      byId?.system &&
      ai.system &&
      byId.system === ai.system &&
      byId.code &&
      ai.code &&
      byId.code !== ai.code,
    )
    const target =
      (idContradictsCode ? undefined : byId) ?? (key ? externalByKey.get(key) : undefined)
    if (target) {
      updatesByRowId.set(target.id, ai)
    } else if (ai.chartId && !idContradictsCode) {
      // The AI targets a chart row the doctor removed from the note — honor the removal.
      continue
    } else {
      newAiRows.push({
        id: crypto.randomUUID(),
        system: ai.system ?? null,
        code: ai.code ?? null,
        display: ai.display,
        clinicalStatus: ai.clinicalStatus,
        reaction: ai.reaction ?? null,
        severity: ai.severity ?? null,
        category: ai.category ?? null,
        // Captured this visit → editable + materialized at Lock. Self-describing
        // so the reopen backfill (legacy-blob only) never touches these rows.
        external: false,
        chartId: null,
        needsReview: ai.uncertain,
        reviewReason: ai.uncertainReason,
      })
    }
  }
  const newAiKeys = new Set(newAiRows.map((a) => clinicalDedupeKey(a)).filter(Boolean))
  const mergedAllergies = [
    ...base.allergies
      .map((row) => {
        const ai = updatesByRowId.get(row.id)
        if (!ai) return row
        const nextStatus = ai.clinicalStatus ?? row.clinicalStatus
        const nextReaction = ai.reaction ?? row.reaction
        const nextSeverity = ai.severity ?? row.severity
        const changed =
          nextStatus !== row.clinicalStatus ||
          nextReaction !== row.reaction ||
          nextSeverity !== row.severity
        return {
          ...row,
          clinicalStatus: nextStatus,
          reaction: nextReaction,
          severity: nextSeverity,
          // An unchanged re-emit (the AI echoing a known allergy verbatim)
          // must NOT badge; a prior update sticks across re-populates.
          updated: row.updated || changed,
        }
      })
      .filter((row) => {
        if (row.external) return true
        const key = clinicalDedupeKey(row)
        return key === null || !newAiKeys.has(key)
      }),
    ...newAiRows,
  ]

  // --- Conditions: same merge rule; duplicates resolve to AI's status. The AI
  //     row REPLACES a colliding carried row (rebuild, not patch), so carry
  //     `external` over from the replaced row — losing it would re-materialize
  //     the chart Condition at Lock. A flipped category still materializes as
  //     an encounter dx (only the pastMedicalHistory path skips external). ---
  const externalConditionKeys = new Set(
    base.conditions
      .filter((c) => c.external)
      .map((c) => clinicalDedupeKey(c))
      .filter(Boolean),
  )
  // The tool schema has no condition note, so the AI can never emit one — a
  // colliding AI row keeps the base row's note instead of wiping it.
  const baseConditionNoteByKey = new Map<string, string>()
  for (const c of base.conditions) {
    const key = clinicalDedupeKey(c)
    if (key && c.note && !baseConditionNoteByKey.has(key)) baseConditionNoteByKey.set(key, c.note)
  }
  const aiConditions = draft.conditions.map((c) => {
    const key = clinicalDedupeKey(c)
    return {
      id: crypto.randomUUID(),
      code: c.code,
      display: c.display,
      category: c.category,
      clinicalStatus: c.clinicalStatus,
      onsetDateTime: c.onsetDateTime ?? null,
      severity: c.severity ?? null,
      rank: c.rank ?? null,
      note: (key && baseConditionNoteByKey.get(key)) || '',
      external: key !== null && externalConditionKeys.has(key),
    }
  })
  const aiConditionKeys = new Set(aiConditions.map((c) => clinicalDedupeKey(c)).filter(Boolean))
  const mergedConditions = [
    ...base.conditions.filter((c) => {
      const key = clinicalDedupeKey(c)
      return key === null || !aiConditionKeys.has(key)
    }),
    ...aiConditions,
  ]

  // PD-2016 — an encounter diagnosis the AI adds reaches the visit's billing lines, as a
  // doctor's add does. With no lines yet the first save links every diagnosis itself.
  const baseDxCodes = new Set(
    base.conditions.filter((c) => c.category === 'encounter-diagnosis').map((c) => c.code),
  )
  const selectedCptCode = getSelectedCandidate(base.billingSuggestion)?.code
  let billingEntries = base.billingEntries
  const billingSync: AiBillingSync = { addedDxCodes: [], changes: [] }
  for (const c of mergedConditions) {
    if (c.category !== 'encounter-diagnosis' || !c.code || baseDxCodes.has(c.code)) continue
    billingSync.addedDxCodes.push(c.code)
    const change: NoteDiagnosisChange = {
      kind: 'add',
      dx: { system: ICD10_SYSTEM, code: c.code, display: c.display },
    }
    const { entries, skipped } = applyNoteDiagnosisChange(billingEntries, change, selectedCptCode)
    if (entries !== billingEntries) billingSync.changes.push(change)
    billingEntries = entries
    // Every add targets the same primary line, so one CPT names all the skipped codes.
    if (skipped) {
      billingSync.capSkipped ??= { codes: [], procedureCode: skipped.procedureCode }
      billingSync.capSkipped.codes.push(c.code)
    }
  }

  // --- Medications: not chart-seeded (carry-forward disabled), so `base`
  //     is empty here and the result is just the AI-captured meds — every
  //     medication discussed this visit, each with the AI's `status`
  //     (Continue / Discontinued / Stopped / etc.). ---
  const aiMedications = draft.medications.map((m) => ({
    id: crypto.randomUUID(),
    code: m.code,
    display: m.display,
    status: m.status,
    intent: m.intent,
    dose: m.dose ?? null,
    // Route comes from the AI when stated; otherwise infer from the RxNorm
    // product display (e.g. "lisinopril 10 MG Oral Tablet" → PO), which is
    // safe because the route is inherent to the product. Dose/frequency are
    // NOT inferred — they reflect what was actually prescribed/said.
    route: m.route ?? extractRouteFromRxNorm(m.display) ?? null,
    frequency: m.frequency ?? null,
    note: m.note ?? null,
    needsReview: m.uncertain,
    reviewReason: m.uncertainReason,
  }))
  const aiMedicationKeys = new Set(aiMedications.map((m) => clinicalDedupeKey(m)).filter(Boolean))
  const mergedMedications = [
    ...base.medications.filter((m) => {
      const key = clinicalDedupeKey(m)
      return key === null || !aiMedicationKeys.has(key)
    }),
    ...aiMedications,
  ]

  // --- Observations: vitals are NOT chart-seeded (carry-forward disabled),
  //     so `base.observations` is empty and the result is just the AI's
  //     this-visit vitals — no prior reading is back-filled for a vital the
  //     AI didn't capture. The dedupe-by-code-or-display merge below is kept
  //     uniform with the other arrays (and still de-dupes if a manual entry
  //     and an AI vital collide). ---
  const aiObservations = draft.observations.map((o) => {
    // A non-vital measurement (a dictated lab, a numeric exam finding) has no
    // typed slot: `draftToSoap` flattens it to "Display: value unit" prose on
    // save. Land it as text so it is editable before the first save exactly as
    // it is after one (PD-1585). Keyed on the code like the save split — a
    // vital LOINC under another category still owns its typed slot.
    const measurementAsText =
      o.category !== 'vital-signs' && o.valueQuantity && !isTypedVitalCode(o.code)
        ? `${o.valueQuantity.value} ${o.valueQuantity.unit}`.trim()
        : null
    return {
      id: crypto.randomUUID(),
      code: o.code,
      display: o.display,
      category: o.category,
      // The AI emits transcript-verbatim units ("lbs", "F", …) — normalize onto
      // the vital's canonical dropdown option so the unit Select can hold it.
      valueQuantity:
        o.valueQuantity && !measurementAsText
          ? { ...o.valueQuantity, unit: normalizeVitalUnit(o.code, o.valueQuantity.unit) ?? '' }
          : null,
      valueString: measurementAsText ?? o.valueString ?? null,
      note: o.note ?? null,
      needsReview: o.implausible || o.uncertain,
      // The reason is what tells a low-confidence vital apart from an implausible one.
      reviewReason: o.uncertain ? (o.uncertainReason ?? LOW_CONFIDENCE_REASON) : undefined,
    }
  })
  const aiObservationKeys = new Set(aiObservations.map((o) => clinicalDedupeKey(o)).filter(Boolean))
  const mergedObservations = [
    ...base.observations.filter((o) => {
      const key = clinicalDedupeKey(o)
      return key === null || !aiObservationKeys.has(key)
    }),
    ...aiObservations,
  ]

  // `noteDate` is NOT seeded here: fresh notes omit it on save (BE anchors to
  // the visit date), and an AI re-populate must not clobber a doctor's edit.

  const values: ProgressNoteEditFormValues = {
    ...base,
    // PD-1613 — an AI draft reached the form; the server keeps this true.
    aiAssisted: true,
    // Defensive — when the AI returns a partial payload (e.g. structured
    // arrays without a summary narrative), preserve whatever the form already
    // had (carry-forward seed or a previous AI populate). Same logic for
    // `billing` below. Without this guard, a partial AI response would
    // overwrite the existing summary / billing with `undefined` and the form
    // would render blank narrative cards.
    // The AI contract's `hpi` is `string[]`; the form uses `{id, text}` rows.
    summary: draft.summary
      ? {
          ...draft.summary,
          hpi: (draft.summary.hpi ?? []).map((text) => ({ id: crypto.randomUUID(), text })),
          // Optional on the AI contract, required (defaulted) on the form.
          mdm: draft.summary.mdm ?? '',
        }
      : base.summary,
    // AI follow-up instructions populate the structured Follow-up section; the
    // form uses `{id, text}` rows. Keep the existing rows when the AI returned
    // none (same defensive-preserve semantics as `summary` / `billing`).
    followUps: draft.summary?.followUp?.length
      ? draft.summary.followUp.map((text) => ({ id: crypto.randomUUID(), text }))
      : base.followUps,
    // Contract types treat scalar fields as `?: T | null` (optional). The
    // form schema is stricter (`T | null` without `?`). Coalesce `undefined`
    // → `null` (or empty string where the form expects a default) at this
    // bridge boundary so RHF sees the canonical shape.
    conditions: mergedConditions,
    billingEntries,
    procedures: draft.procedures.map((p) => ({
      id: crypto.randomUUID(),
      code: p.code,
      display: p.display,
      status: p.status,
      performedDateTime: p.performedDateTime ?? null,
    })),
    medications: mergedMedications,
    observations: mergedObservations,
    allergies: mergedAllergies,
    serviceRequests: draft.serviceRequests.map((s) => ({
      id: crypto.randomUUID(),
      system: s.system,
      code: s.code,
      display: s.display,
      category: s.category,
      intent: s.intent,
      priority: s.priority ?? 'routine',
      reasonText: s.reasonText ?? null,
    })),
    // Same defensive coalesce as `summary` — keep the form's existing
    // billing suggestion when the AI didn't return one. Total Time arrives as
    // `{label, minutes}` pairs and becomes RHF rows (PD-1321).
    billingSuggestion: draft.billing
      ? {
          ...draft.billing,
          providerTimeEntries: toTimeEntryRows(draft.billing.totalTime?.providerEntries),
          careCoordinatorTimeEntries: toTimeEntryRows(
            draft.billing.totalTime?.careCoordinatorEntries,
          ),
        }
      : base.billingSuggestion,
  }
  return { values, billingSync }
}

/** What an AI merge did to the visit's billing lines (PD-2016). */
type AiBillingSync = {
  /** Encounter-diagnosis codes the draft added to the Problem List. */
  addedDxCodes: string[]
  /** Codes left off billing because the primary line already holds the cap. */
  capSkipped?: { codes: string[]; procedureCode: string }
  /** The adds that reached a billing line, queued for the billing re-read to replay. */
  changes: NoteDiagnosisChange[]
}

const BILLING_WITHHELD_NOTICE =
  'Billing not updated: a diagnosis on this visit could not be loaded. Update it in Edit billing.'

const capSkippedNotice = (codes: string[], procedureCode: string) =>
  `${codes.join(', ')} not added to billing: ${procedureCode} already has ${MAX_DIAGNOSIS_POINTERS_PER_LINE} diagnoses. Add it in Edit billing.`

/** PD-2016 — tell the provider when an AI merge's diagnoses did not reach billing. */
function notifyAiBillingSync(sync: AiBillingSync, billingWithheld: boolean) {
  // PD-1958 withhold: saves leave billing out, so none of the AI's diagnoses reach the Claim.
  if (billingWithheld) {
    if (sync.addedDxCodes.length) {
      showNotification({ color: 'yellow', message: BILLING_WITHHELD_NOTICE })
    }
    return
  }
  if (sync.capSkipped) {
    showNotification({
      color: 'yellow',
      message: capSkippedNotice(sync.capSkipped.codes, sync.capSkipped.procedureCode),
    })
  }
}

/**
 * Carry-forward payload seeded from the patient's current chart — limited to
 * the patient's SAFETY CONTEXT: active AllergyIntolerance + Condition
 * (problem-list). These are always relevant to the note and are rarely
 * re-elicited aloud every follow-up, so seeding them keeps the note's safety
 * picture complete.
 *
 * Medications and vitals are intentionally NOT carried forward. They're
 * visit-specific: auto-listing the full active pharmacy under "Meds Reviewed"
 * asserts a reconciliation that may not have happened, and showing a prior
 * vital reading implies it was measured today (especially wrong on a virtual
 * visit). Those sections therefore come only from the AI draft / manual entry.
 *
 * Fetched once at form mount, merged with the AI draft via
 * `applyAiDraftToFormValues`. The shapes mirror the form's structured arrays
 * so the hydrate path can splice them straight into
 * `makeEmptyProgressNoteFormValues(...)` without further conversion.
 */
type CarryForwardSeed = Pick<
  ProgressNoteEditFormValues,
  'allergies' | 'conditions' | 'familyHistory'
>

function pickCoding(
  concept: { coding?: Array<{ system?: string; code?: string; display?: string }> } | undefined,
): { system?: string; code?: string; display?: string } | undefined {
  return concept?.coding?.[0]
}

/**
 * Dedupe a carry-forward list by `(code, display)` tuple. Keeps the first
 * occurrence per key, drops subsequent ones. Items without a code or display
 * fall through unchanged (treated as unique).
 *
 * Why: the patient's chart can accumulate duplicate Conditions /
 * MedicationStatements when the Progress Note cascade creates new resources
 * on every save instead of upserting by code. Until the BE cascade is fixed,
 * dedup here so the doctor doesn't see (e.g.) 113 identical Obesity entries
 * in the Problem List. The first occurrence wins — when searches are sorted
 * by recency that's the most-recent record, which is what we want carried
 * forward.
 */
function dedupeByCodeAndDisplay<T extends { code?: string | null; display?: string | null }>(
  items: T[],
): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const item of items) {
    const code = item.code?.trim() ?? ''
    const display = item.display?.trim().toLowerCase() ?? ''
    if (!code && !display) {
      // No identifier — pass through (can't dedupe what we can't key).
      out.push(item)
      continue
    }
    const key = `${code}|${display}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }
  return out
}

function carryForwardFromAllergies(
  allergies: AllergyIntolerance[],
): ProgressNoteEditFormValues['allergies'] {
  const items = allergies.map((a) => {
    const coding = pickCoding(a.code)
    return {
      id: crypto.randomUUID(),
      system: coding?.system ?? null,
      code: coding?.code ?? null,
      display: coding?.display || a.code?.text || 'Unknown allergy',
      clinicalStatus: 'active' as const,
      reaction: a.reaction?.[0]?.manifestation?.[0]?.text ?? null,
      severity: a.reaction?.[0]?.severity ?? null,
      category: a.category?.[0] ?? null,
      // Every carry-forward row already lives on the chart, so it's read-only
      // in the note and excluded from Lock re-materialization (PD-1141) —
      // regardless of whether it was imported (HG) or entered in beHuman.
      external: true,
      // Source chart row id — lets an AI-reported change target this exact row.
      chartId: a.id ?? null,
    }
  })
  return dedupeByCodeAndDisplay(items)
}

/**
 * Backfill `external` on hydrated allergy rows that predate the flag (old
 * blobs): a row matching an active chart AllergyIntolerance by `(code,
 * display)` came from the chart, so it's read-only (PD-1141). Rows with a
 * persisted flag keep it. Also stamps `chartId` from the matched chart
 * resource — both on the pre-flag rows and on already-`external` rows from
 * blobs saved before `chartId` existed — so AI update-in-place can target
 * the exact chart row on reopen.
 */
function backfillAllergyExternalFlags(
  rows: ProgressNoteEditFormValues['allergies'],
  chartAllergies: AllergyIntolerance[],
): ProgressNoteEditFormValues['allergies'] {
  const chartIdByKey = new Map<string, string | undefined>()
  for (const a of chartAllergies) {
    const coding = pickCoding(a.code)
    const code = coding?.code?.trim() ?? ''
    const display = (coding?.display || a.code?.text || '').trim().toLowerCase()
    if ((code || display) && !chartIdByKey.has(`${code}|${display}`)) {
      chartIdByKey.set(`${code}|${display}`, a.id)
    }
  }
  if (chartIdByKey.size === 0) return rows
  return rows.map((row) => {
    const key = `${row.code?.trim() ?? ''}|${(row.display ?? '').trim().toLowerCase()}`
    if (row.external === undefined) {
      return chartIdByKey.has(key)
        ? { ...row, external: true, chartId: chartIdByKey.get(key) ?? null }
        : row
    }
    // Already-shipped-era blob: flagged external but never stamped with the id.
    if (row.external && !row.chartId) {
      const chartId = chartIdByKey.get(key)
      return chartId ? { ...row, chartId } : row
    }
    return row
  })
}

function carryForwardFromConditions(
  conditions: Condition[],
): ProgressNoteEditFormValues['conditions'] {
  const items = conditions.map((c) => {
    const coding = pickCoding(c.code)
    return {
      id: crypto.randomUUID(),
      code: coding?.code ?? '',
      display: coding?.display || c.code?.text || 'Unknown problem',
      // Problem-list carry-forward → `problem-list-item`. The AI draft's
      // overlay can flip this to `encounter-diagnosis` for new this-visit
      // diagnoses without disturbing the carry-forward seeds.
      category: 'problem-list-item' as const,
      clinicalStatus: 'active' as const,
      onsetDateTime: c.onsetDateTime ?? null,
      severity: null,
      rank: null,
      note: '',
      // Mirrors an existing chart Condition, so the pastMedicalHistory path
      // skips it at Lock — re-materializing would fork a duplicate.
      external: true,
    }
  })
  return dedupeByCodeAndDisplay(items)
}

/**
 * Backfill `external` on hydrated condition rows that predate the flag (old
 * blobs): a problem-list row matching an active chart problem-list Condition
 * by `(code, display)` came from the chart carry-forward, so it must stay
 * skipped at Lock. Rows with a persisted flag keep it.
 */
function backfillConditionExternalFlags(
  rows: ProgressNoteEditFormValues['conditions'],
  chartConditions: Condition[],
): ProgressNoteEditFormValues['conditions'] {
  const chartKeys = new Set<string>()
  for (const c of chartConditions) {
    const coding = pickCoding(c.code)
    const code = coding?.code?.trim() ?? ''
    const display = (coding?.display || c.code?.text || '').trim().toLowerCase()
    if (code || display) chartKeys.add(`${code}|${display}`)
  }
  if (chartKeys.size === 0) return rows
  return rows.map((row) => {
    if (row.external !== undefined || row.category !== 'problem-list-item') return row
    const key = `${row.code?.trim() ?? ''}|${(row.display ?? '').trim().toLowerCase()}`
    return chartKeys.has(key) ? { ...row, external: true } : row
  })
}

// Placeholder chart rows that would only add noise to the note (PD-1377).
const FMH_NOISE_CONDITIONS = new Set([
  'no known problems',
  'no current problems or disability',
  'other',
  'n/a',
])
// Rows whose relation can't name a person ("family member", bare code fallbacks).
const FMH_GENERIC_RELATIVES = new Set(['family member', 'other', '19'])

/**
 * Chart `FamilyMemberHistory` → grouped read-only note rows (PD-1377):
 * one row per relative, conditions deduped, noise/unresolvable rows dropped.
 * Imports duplicate relatives freely (e.g. `FTH` vs `NFTH` "natural father"
 * are the same human relation), so relatives key case-insensitively after
 * stripping the `natural` qualifier.
 */
function carryForwardFromFamilyHistory(
  rows: FamilyMemberHistory[],
): ProgressNoteEditFormValues['familyHistory'] {
  const byRelative = new Map<
    string,
    { relative: string; conditions: string[]; seen: Set<string>; deceased: boolean }
  >()
  for (const row of rows) {
    if (row.status === 'entered-in-error') continue
    const rawRelative =
      row.name?.trim() ||
      row.relationship?.text?.trim() ||
      row.relationship?.coding?.[0]?.display?.trim() ||
      ''
    const relative = rawRelative.replace(/^natural\s+/i, '')
    if (!relative || FMH_GENERIC_RELATIVES.has(relative.toLowerCase())) continue
    const key = relative.toLowerCase()
    const group = byRelative.get(key) ?? {
      relative: relative.charAt(0).toUpperCase() + relative.slice(1),
      conditions: [],
      seen: new Set<string>(),
      deceased: false,
    }
    for (const condition of row.condition ?? []) {
      // NOT `text` alone — HG-coded rows often carry only `coding[0]`.
      const label =
        condition.code?.text?.trim() ||
        condition.code?.coding?.[0]?.display?.trim() ||
        condition.code?.coding?.[0]?.code?.trim() ||
        ''
      if (!label || FMH_NOISE_CONDITIONS.has(label.toLowerCase())) continue
      if (group.seen.has(label.toLowerCase())) continue
      group.seen.add(label.toLowerCase())
      group.conditions.push(label)
    }
    if (row.deceasedBoolean === true) group.deceased = true
    byRelative.set(key, group)
  }
  return [...byRelative.values()]
    .filter((group) => group.conditions.length > 0)
    .map((group) => ({
      id: crypto.randomUUID(),
      relative: group.relative,
      conditions: group.conditions,
      deceased: group.deceased ? true : undefined,
    }))
}

/**
 * Build a fresh `ProgressNoteEditFormValues` snapshot from the empty default
 * + the patient's safety-context carry-forward seeds (allergies + problems +
 * family history). Medications and vitals are left at the empty default —
 * carry-forward is disabled for them; the AI overlay / manual entry fills
 * those sections. Used by the create-mode hydrate branch before any AI
 * overlay is applied.
 */
function seedFormWithCarryForward(
  empty: ProgressNoteEditFormValues,
  seed: CarryForwardSeed,
): ProgressNoteEditFormValues {
  return {
    ...empty,
    allergies: seed.allergies,
    conditions: seed.conditions,
    familyHistory: seed.familyHistory,
  }
}

import {
  AssessmentSection,
  EncounterDetailsSection,
  AiDisclosureSection,
  FollowUpSection,
  MdmAndTimeSection,
  ObjectiveSection,
  PlanSection,
  SubjectiveSection,
} from './ProgressNoteSections'
import { TableOfContents } from '../ProgressNoteViewer/TableOfContents'
import { TocPresenceProvider } from '../ProgressNoteViewer/TocPresenceProvider'
import { NoteStatsStripFormBridge } from '../ProgressNoteViewer/NoteStatsStrip'
import { draftToSoap, isTypedVitalCode, type SoapNoteDto } from './draftToSoap'
import {
  CARE_COORDINATOR_ENCOUNTER_CLASS_CODE,
  CARE_COORDINATOR_ENCOUNTER_CLASS_SYSTEM,
  IMPORTED_TAG_CODE,
  IMPORTED_TAG_SYSTEM,
  MAX_DIAGNOSIS_POINTERS_PER_LINE,
} from '@/model/middleware-types'
import { useSubmittedChargeQuote } from './useSubmittedChargeQuote'
import { useRenderingProviderId } from './useRenderingProviderId'
import {
  hydrateAllergiesFromQr,
  hydrateConditionsFromQr,
  hydrateMedicationsFromQr,
  hydrateProceduresFromQr,
  hydrateServiceRequestsFromQr,
  hydrateVitalObservationsFromQr,
} from './progressNoteQrHydrate'
import {
  hasUnavailableDiagnoses,
  toBillingEntries,
  toFormBillingSuggestion,
} from '@/components/billing'
import {
  buildExamNarrativeRow,
  LOW_CONFIDENCE_REASON,
  hydrateAllergiesFromScribeDraft,
  hydrateBillingEntriesFromScribeDraft,
  hydrateConditionsFromScribeDraft,
  hydrateExamNarrativeFromScribeDraft,
  hydrateFamilyHistoryFromScribeDraft,
  hydrateFollowUpsFromScribeDraft,
  hydrateMedicationsFromScribeDraft,
  hydrateProceduresFromScribeDraft,
  hydrateRosFromScribeDraft,
  hydrateServiceRequestsFromScribeDraft,
  hydrateSummaryFromScribeDraft,
  hydrateTimeEntriesFromScribeDraft,
  hydrateVitalObservationsFromScribeDraft,
  buildExistingSoapSnapshot,
  readScribeDraftPayload,
  refreshExistingSoapSnapshot,
  splitHpiParagraphs,
  toTimeEntryRows,
} from './scribeDraftPayload'
import { normalizeVitalUnit } from './vitalUnits'
import { extractRouteFromRxNorm } from './rxnormPrefill'

type ProgressNoteEditProps = {
  /**
   * Existing DocumentReference to hydrate from (Documents.tsx edit flow).
   * When omitted the form renders empty/draft — used by the in-call
   * `<EncounterWorkspace>` and the chart "New Encounter" side panel where
   * no DocRef exists until the doctor submits.
   */
  documentReference?: DocumentReference
  /**
   * Required when `documentReference` is provided — drives hydrate + edit
   * enablement. When both are absent the form is in "create" mode
   * (`canEdit=true`, fresh empty state).
   */
  provenance?: Provenance
  onSave?: () => void
  /**
   * Optional visit context fed in by `<EncounterWorkspace>` for the
   * create-mode submit path. When `encounterId` is set the submit posts to
   * `/visits/{encounterId}/soap-note` (call-page in-progress encounter);
   * when it's absent the submit posts to `/visits/soap-note` which creates
   * a new Encounter implicitly (chart side-panel new-encounter case).
   */
  patientId?: string
  encounterId?: string
  practitionerId?: string
  organizationId?: string
  projectId?: string
  /** Called after a successful submit. Alias for `onSave` exposed for the
   *  workspace shell whose API is `onFinish`. */
  onFinish?: () => void
  /**
   * Called when the user clicks the "Cancel" button in the mockup-parity
   * edit-mode top action bar. When omitted, the Cancel button is not
   * rendered — the parent owns the exit-edit UX (e.g. Documents.tsx
   * already has a separate Edit/Cancel toggle outside this component, so
   * passing `onCancel` would surface a duplicate control). The in-call
   * `<EncounterWorkspace>` shell + future standalone edit panels can wire
   * this to bail out of edit mode.
   */
  onCancel?: () => void
  /**
   * Drop the Paper's default top margin. The call pane starts flush under the
   * patient header, where `mt="lg"` reads as a floating white stripe.
   */
  flushTop?: boolean
  /**
   * PD-1440 — a save was refused with 409 (the visit already has a locked
   * note). Not transient like other autosave failures: the parent re-resolves
   * the note state and closes this editor.
   */
  onSaveConflict?: () => void
  /**
   * PD-1330 — first open of an auto-created (never-reviewed) draft: apply the
   * stored AI draft through the normal disclaimer + merge + forced flush once
   * the artifact resolves. The flush's save clears the unreviewed tag.
   */
  autoApplyAiDraft?: boolean
}

type VisitType = 'new' | 'follow-up'

function inferVisitType(documentReference: DocumentReference): VisitType {
  const typeText = `${documentReference.type?.text || ''} ${documentReference.type?.coding
    ?.map((coding) => coding.display || '')
    .join(' ')}`.toLowerCase()
  return typeText.includes('new') ? 'new' : 'follow-up'
}

/**
 * Create-mode visit type: `'follow-up'` if the patient has a prior `finished`
 * encounter in the last 3 years (the CMS established-patient window), else
 * `'new'`. Care-coordinator and Health-Gorilla-imported encounters are excluded
 * — neither is a prior clinical visit with a provider. Doctor-editable.
 */
async function deriveInitialVisitTypeFromHistory(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string,
  currentEncounterId?: string,
): Promise<VisitType> {
  const since = new Date()
  since.setFullYear(since.getFullYear() - 3)
  // Ids, not `_summary=count`: THIS encounter is already `finished` here, so a
  // bare count includes it and labels every new patient a follow-up. `_count=2`
  // because one row may be this encounter.
  const recent = await medplum.searchResources(
    'Encounter',
    `subject=Patient/${patientId}&status=finished&date=ge${since.toISOString().slice(0, 10)}` +
      `&class:not=${CARE_COORDINATOR_ENCOUNTER_CLASS_SYSTEM}|${CARE_COORDINATOR_ENCOUNTER_CLASS_CODE}` +
      `&_tag:not=${IMPORTED_TAG_SYSTEM}|${IMPORTED_TAG_CODE}&_elements=id&_count=2`,
  )
  const hasPriorFinished = recent.some((enc) => enc.id && enc.id !== currentEncounterId)
  return hasPriorFinished ? 'follow-up' : 'new'
}

function getProvenanceTargetRef(provenance: Provenance | undefined, prefix: string) {
  return provenance?.target
    ?.map((target) => target.reference)
    .find((reference) => reference?.startsWith(`${prefix}/`))
}

function getEncounterRef(provenance: Provenance | undefined) {
  return provenance?.entity
    ?.map((entity) => entity.what?.reference)
    .find((reference) => reference?.startsWith('Encounter/'))
}

/** PD-1440 — matches the middleware's `ORPCError('CONFLICT')` → HTTP 409. */
function isConflictError(error: unknown): boolean {
  const status = (error as { status?: number; statusCode?: number })?.status
  return status === 409 || (error as { statusCode?: number })?.statusCode === 409
}

function getQuestionnaireStringAnswer(
  response: QuestionnaireResponse | undefined,
  linkId: string,
): string {
  if (!response?.item?.length) {
    return ''
  }
  const queue: QuestionnaireResponseItem[] = [...response.item]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue
    if (current.linkId === linkId) {
      return current.answer?.[0]?.valueString || ''
    }
    if (current.item?.length) {
      queue.push(...current.item)
    }
  }
  return ''
}

export function ProgressNoteEdit({
  documentReference,
  provenance,
  onSave,
  patientId: patientIdProp,
  encounterId: encounterIdProp,
  practitionerId: practitionerIdProp,
  organizationId: organizationIdProp,
  projectId: projectIdProp,
  onFinish,
  onCancel,
  flushTop,
  onSaveConflict,
  autoApplyAiDraft,
}: ProgressNoteEditProps) {
  const medplum = useMedplum()
  const client = useClient()
  const saveProgressNoteDraft = useSaveProgressNoteDraft()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const orpc = useOrpc()
  // FE-4 — Read the live scribe session id so the SOAP submit can hand it to
  // the middleware in `visitContext.scribeSessionId`. The middleware reads
  // this body field to gate emission of the indigo "AI" pill via the
  // `Device/scribe-ai-v1` Provenance agent. When no scribe session is active
  // (manual create-mode save without scribe, edit-mode save of an older
  // note), `scribeSessionId` is `null` and we omit the field from the body
  // — the backend gate treats `undefined` and `null` identically (no pill).
  const { scribeSessionId } = useCallSession()
  const [submitting, setSubmitting] = useState(false)
  /**
   * Subtle draft auto-save indicator state. `idle` renders nothing; `saving`
   * shows a spinning "Saving…"; `saved` shows a green check "Saved" after a
   * successful debounced save. Never shown for a `final` (locked) note — those
   * are never auto-saved.
   */
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  /**
   * Serialized snapshot of the last `SoapNoteDto` we persisted (explicit Save,
   * auto-save, or AI auto-submit). The debounced auto-save compares the current
   * `draftToSoap(...)` output against this and SKIPS the POST when nothing
   * changed, so an idle form (or a programmatic `methods.reset` that doesn't
   * alter content) never triggers a redundant network write.
   */
  const lastSavedSnapshotRef = useRef<string | null>(null)
  /**
   * PD-2031 — the note this editor writes: the one it opened, else the one its first save
   * created. Sent with every draft save so a save to a note removed from the chart is refused
   * rather than filed as a new draft.
   */
  const noteIdRef = useRef<string | undefined>(documentReference?.id)
  /**
   * PD-1958 — the form's billing came from a read that left out a diagnosis it could not
   * load, so saves leave billing out (the Claim keeps it) until a complete re-read.
   */
  const billingIncompleteRef = useRef(false)
  /**
   * PD-2016 — Problem List billing syncs that no successful save has carried yet. A billing
   * re-read replays them on top of the server's lines, so the refetch of an earlier save
   * cannot drop them before their own debounced save goes out.
   */
  const pendingDiagnosisSyncsRef = useRef<{ seq: number; change: NoteDiagnosisChange }[]>([])
  const diagnosisSyncSeqRef = useRef(0)
  const queueDiagnosisSync = useCallback((change: NoteDiagnosisChange) => {
    diagnosisSyncSeqRef.current += 1
    pendingDiagnosisSyncsRef.current.push({ seq: diagnosisSyncSeqRef.current, change })
  }, [])
  // An AI merge's billing adds are queued like a doctor's, unless saves withhold billing.
  const queueAiBillingSyncs = useCallback(
    (sync: AiBillingSync) => {
      if (billingIncompleteRef.current) return
      sync.changes.forEach(queueDiagnosisSync)
    },
    [queueDiagnosisSync],
  )
  /** Guards the debounced auto-save against firing after unmount. */
  const autoSaveActiveRef = useRef(true)
  /**
   * Holds the latest awaitable auto-save closure so the exit paths (Cancel /
   * unmount) can flush pending edits to the server WITHOUT going through the
   * debounce (whose `.flush()` returns `void` and can't be awaited). The
   * debounced `runAutoSave` and the blocking `flushPendingSave` both call
   * this. Pointed at the current closure on every render so it never reads a
   * stale `methods` / props snapshot.
   */
  const performAutoSaveRef = useRef<((force?: boolean) => Promise<boolean>) | null>(null)
  /** Holds the latest hydrated `SoapNote` snapshot so the adapter can preserve
   * scalar fields the AI didn't touch (e.g. existing vitals notes the doctor
   * filled before the AI draft populated the form). */
  const existingSoapSnapshotRef = useRef<Partial<SoapNoteDto>>({})
  /** Encounter snapshot — resolved from `provenance.entity[…].what` once on
   * hydrate. Drives the Session Duration card + the encounter-scoped FHIR
   * count queries in `<NoteStatsStrip>`. */
  const [encounter, setEncounter] = useState<Encounter | undefined>(undefined)
  // Mirrors `LockedProgressNoteSections` — encounter date or `—`.
  const encounterDateDisplay = useMemo(() => {
    const start = encounter?.period?.start
    return start ? formatDateTime(start, '-', true) : '—'
  }, [encounter?.period?.start])
  /**
   * Flag flipped to `true` the moment this component first applies a scribe
   * AI draft to the form — via either the bridge's cached `consumeAiDraft()`
   * (mount-time race fix) or the `registerPopulateFromAi` callback
   * (mid-edit re-populate). Used by the hydrate effect to skip its empty
   * `methods.reset(...)` on subsequent re-runs (e.g. when `initialNoteDate`
   * crosses a minute boundary), which would otherwise wipe the AI values.
   */
  const aiDraftAppliedRef = useRef(false)
  /**
   * FE-3 — `true` once the create-mode hydrate effect has fetched the
   * patient's carry-forward chart data and reset the form. Edit-mode skips
   * the carry-forward fetch (the existing DocRef already captures the
   * note's snapshot) and flips this true once the QR/Claim hydrate
   * completes. Used to gate the form render so the doctor never sees a
   * brief empty-form flash before the carry-forward materializes.
   */
  const [hydrated, setHydrated] = useState(false)

  // Create-mode defaults: now + follow-up. Edit-mode pulls from the DocRef.
  // Memoized on `documentReference` so the create-mode `new Date()` is captured
  // ONCE at mount rather than recomputed every render. Recomputing produced a
  // fresh minute-precision string each minute which — being in the hydrate
  // effect's dep array — re-fired the effect and `reset()` the form, silently
  // wiping a manually-typed note on the create-mode path. See round-4 review #3.
  const initialNoteDate = useMemo(
    () =>
      documentReference
        ? toClinicDateTimeLocal(documentReference.date)
        : toClinicDateTimeLocal(new Date().toISOString()),
    [documentReference],
  )
  const initialVisitType: VisitType = useMemo(
    () => (documentReference ? inferVisitType(documentReference) : 'follow-up'),
    [documentReference],
  )

  const methods = useForm<ProgressNoteEditFormValues>({
    // The schema uses `.default(…)` extensively, so `z.input` (partial) and
    // `z.output` (required-after-defaults) diverge. RHF's resolver type is
    // keyed off the `Input` half; the runtime is happy with the `Output`
    // half. One-line cast through `Resolver<any>` to keep TS calm.
    resolver: zodResolver(
      progressNoteEditFormSchema,
    ) as unknown as import('react-hook-form').Resolver<ProgressNoteEditFormValues>,
    defaultValues: makeEmptyProgressNoteFormValues(initialNoteDate, initialVisitType),
    mode: 'onSubmit',
  })

  const conditionsField = useFieldArray({ control: methods.control, name: 'conditions' })
  const medicationsField = useFieldArray({ control: methods.control, name: 'medications' })
  const serviceRequestsField = useFieldArray({ control: methods.control, name: 'serviceRequests' })
  const followUpsField = useFieldArray({ control: methods.control, name: 'followUps' })
  const observationsField = useFieldArray({ control: methods.control, name: 'observations' })
  const allergiesField = useFieldArray({ control: methods.control, name: 'allergies' })

  // Create-mode (no DocRef) always allows editing. Edit-mode requires a
  // Provenance to be able to round-trip writes back to the QR/Claim chain.
  const isCreateMode = !documentReference
  const canEdit = isCreateMode || Boolean(provenance)

  // RHF's `formState` is a lazy Proxy: `isDirty` is only kept computed once
  // something SUBSCRIBES to it — a bare `methods.formState.isDirty` read
  // inside a callback sees a stale `false` until then. This hook subscribes
  // from first render, making the callback reads (autosave baseline + the AI
  // load gate below) reliable from the first keystroke.
  const { isDirty: formIsDirty } = useFormState({ control: methods.control })

  // --- AI-draft actions (header menu) --------------------------------------
  // Which scribe artifacts exist for this encounter. Same hook/search the call
  // rail uses; `undefined` (locked note, no edit rights, or no encounter)
  // skips the lookup entirely and keeps the menu hidden. Edit-mode resolves
  // the encounter from the Provenance, create-mode from the prop — must stay
  // in lockstep with `autoSaveEncounterId` below.
  const aiActionsEncounterId =
    documentReference?.docStatus === 'final' || !canEdit
      ? undefined
      : isCreateMode
        ? encounterIdProp
        : getEncounterRef(provenance)?.split('/')[1]
  const {
    state: scribeArtifactResolveState,
    draftDocRefId: aiDraftDocRefId,
    transcriptDocRefId: aiTranscriptDocRefId,
    noteId: aiNoteId,
  } = useEncounterScribeState(aiActionsEncounterId)

  // PD-1516 — AI Dictate. Hidden during a live scribe session (the Zoom call
  // owns the microphone) and wherever the browser lacks speech recognition.
  // PD-1516 flow (b) impl lives below (it needs the apply machinery declared
  // later in this scope); the hook mounts here, so it reaches it through a ref.
  const organizeDictationImplRef = useRef<
    ((dictation: string, isStale: () => boolean) => Promise<boolean>) | null
  >(null)
  const dictate = useAiDictate({
    enabled: canEdit && !scribeSessionId,
    isCreateMode,
    encounterId: aiActionsEncounterId,
    methods,
    client,
    onOrganizeDictation: (dictation, isStale) =>
      organizeDictationImplRef.current?.(dictation, isStale) ?? Promise.resolve(false),
  })
  const showAiDictate =
    canEdit && !scribeSessionId && dictate.supported && Boolean(aiActionsEncounterId)

  const { generateFromTranscript, generateFromDictation, draftProgress } = useFinalizeScribe()
  const [aiAction, setAiAction] = useState<'load' | 'regenerate' | null>(null)
  /** Latch set synchronously on click — `aiAction` only flips after the confirm
   * resolves, so a second click during the confirm window would stack a second
   * modal. */
  const aiActionInFlightRef = useRef(false)
  /** The overlay card is viewport-fixed (the Paper is several viewports tall);
   * this rect centers it over the NOTE — viewport-center lands left/right of
   * the note next to the chart sidebar or the call rail. */
  const paperRef = useRef<HTMLDivElement | null>(null)
  // A regenerate runs ~60-90s — long enough for a window resize or a panel
  // toggle to move the note under the fixed card. Nudge a re-render on either
  // signal so the render-time rect read is re-measured.
  const [, bumpCardReposition] = useState(0)
  useEffect(() => {
    if (!aiAction || !paperRef.current) return
    const remeasure = () => bumpCardReposition((n) => n + 1)
    window.addEventListener('resize', remeasure)
    const observer = new ResizeObserver(remeasure)
    observer.observe(paperRef.current)
    return () => {
      window.removeEventListener('resize', remeasure)
      observer.disconnect()
    }
  }, [aiAction])
  // A live scribe session owns the CallSession state these actions would
  // clobber; the menu returns when the session ends.
  const showAiMenu = !scribeSessionId && Boolean(aiDraftDocRefId || aiTranscriptDocRefId)

  const patientRefFromDoc = documentReference?.subject?.reference
  const patientIdFromDoc = patientRefFromDoc?.startsWith('Patient/')
    ? patientRefFromDoc.split('/')[1]
    : undefined
  const patientId = patientIdFromDoc ?? patientIdProp

  // PD-1597 — credential-tiered submitted charges for the note's billing codes;
  // the catalog base price is the fallback while the quote loads.
  const watchedBillingEntries = useWatch({ control: methods.control, name: 'billingEntries' })
  const watchedBillingSuggestion = useWatch({ control: methods.control, name: 'billingSuggestion' })
  // Every code the note can bill — entries, all AI candidates and add-ons — so a
  // chip switch or a modal pick finds its quote instead of the catalog fallback.
  const billingCodes = useMemo(
    () =>
      [
        ...(watchedBillingEntries ?? []).map((entry) => entry.procedure?.code),
        ...(watchedBillingSuggestion?.candidates ?? []).map((c) => c.code),
        ...(watchedBillingSuggestion?.addOnCodes ?? []).map((a) => a.code),
      ].filter((code): code is string => Boolean(code)),
    [watchedBillingEntries, watchedBillingSuggestion],
  )
  // The charge tier follows the rendering provider, not the person typing — an
  // assisting nurse must not re-price the treating physician's line (PD-1647).
  const renderingProviderId = useRenderingProviderId({
    encounter,
    encounterId: encounterIdProp,
    fallbackId: medplum.getProfile()?.id,
  })
  // Lines are priced from the quote only. The catalog holds the MD/DO rate, so pricing from
  // it while the quote is in flight saved NP/PA lines at the physician charge, and a saved
  // amount is never re-priced. An unquoted line saves at $0 and fills on the next save once
  // the quote lands; Lock refuses a $0 line with its reason.
  const {
    quote: chargeQuote,
    priceFor,
    refusedFor,
  } = useSubmittedChargeQuote({
    codes: billingCodes,
    practitionerId: renderingProviderId,
    patientId,
    encounterId: encounter?.id ?? encounterIdProp,
    organizationId: organizationIdProp,
  })

  // --- Wire the scribe form bridge so /finalize → /generate-draft can
  //     push the structured AI draft straight into the form state. ---
  const bridge = useScribeFormBridge()

  // `scribeBacked` controls the `<NoteStatsStrip>` visibility (LD-7).
  //
  // A note is scribe-backed when EITHER:
  //   1. The Provenance carries a `Device` agent (the BeHuman AI Scribe
  //      device, seeded server-side — its identifier `scribe-ai-v1`). The
  //      Reference can be by literal `Device/...` or by `identifier`; we
  //      detect either by checking that the agent's `who.reference` starts
  //      with `Device/` OR the agent carries `identifier.value`.
  //   2. The component is mounted under the scribe workspace's
  //      `<ScribeFormBridgeProvider>`. We use the bridge's presence as the
  //      cheap detector for the in-call edit-mid-flow scenario; the strip
  //      then falls back to the form's structured arrays for counts while
  //      the encounter is still empty server-side.
  const provenanceHasScribeDevice = Boolean(
    provenance?.agent?.some(
      (a) =>
        a.who?.reference?.startsWith('Device/') ||
        (a.who?.identifier?.value && a.who.identifier.value.includes('scribe-ai')),
    ),
  )
  const scribeBacked = provenanceHasScribeDevice || Boolean(bridge)

  // `handleSubmitRef` holds the most recent submit function so the populate
  // callback (which captures stale closures at registration time) can always
  // call the latest version with the latest `patientId`/`encounterId`/etc.
  // Assigned below right after `handleSubmit` is declared.
  const handleSubmitRef = useRef<((values: ProgressNoteEditFormValues) => Promise<void>) | null>(
    null,
  )

  useEffect(() => {
    if (!bridge) return
    bridge.registerPopulateFromAi(async (draft, opts) => {
      // Apply the AI draft atomically via `methods.reset(merged)` instead of
      // 8 sequential `methods.setValue(...)` calls. Two reasons:
      //
      //  1. Atomic — a single reset commits all AI arrays in one RHF batch,
      //     avoiding interleaved re-renders that the field-array subscribers
      //     would otherwise process between setValue calls.
      //  2. Dominant — under React 19's concurrent scheduler, the bridge's
      //     `queueMicrotask` flush is not guaranteed to fire AFTER the form's
      //     sibling hydrate effect runs `methods.reset(empty)`. Using
      //     `methods.reset(merged)` here means even when the populate path
      //     runs SECOND (after the empty reset), the AI values still win. And
      //     when this populate path runs FIRST (before hydrate's empty
      //     reset), the hydrate effect's `bridge.hasPendingAiDraft()` guard
      //     skips the wipe.
      //
      // Scalar form-only fields the AI doesn't touch (noteDate, visitType,
      // billingEntries, encounterDetails, followUps) carry through from
      // `methods.getValues()` so prior doctor edits survive an AI re-populate
      // during an open edit session.
      //
      // FE-3 note — when the create-mode hydrate ran first, `methods.getValues()`
      // already returns `carry-forward + manual edits`. Passing that as `base`
      // to `applyAiDraftToFormValues` yields:
      //   carry-forward items that are NOT AI duplicates + manual edits that
      //   are NOT AI duplicates + AI items.
      // That's the right semantic: AI wins on duplicates, but everything
      // else (chart carry-forward, doctor's manual additions) survives.
      const current = methods.getValues()
      const { values: merged, billingSync } = applyAiDraftToFormValues(current, draft)
      queueAiBillingSyncs(billingSync)
      methods.reset(merged)
      aiDraftAppliedRef.current = true
      notifyAiBillingSync(billingSync, billingIncompleteRef.current)

      // FE-4 — Auto-submit handoff. When the End-Session "Create Progress
      // Note" flow asks the bridge to also kick off the save, we fire the
      // same code path the Save button uses and AWAIT it so the Zoom panel's
      // spinner overlay can stay up until navigation fires (no flash of the
      // edit form between "Generating…" and the read-only viewer).
      //
      // On success, `handleSubmit` calls `navigate(...)` internally which
      // unmounts this component; the awaited promise resolves and ZoomPanel
      // tears Zoom down. On failure, `handleSubmit` shows the red toast and
      // returns normally — the edit form stays visible so the doctor can
      // recover.
      if (opts?.autoSubmit) {
        // `merged` already represents the final form state we want to POST.
        // Skip `methods.handleSubmit(onConfirmSubmit)` because that path runs
        // through Zod first; in create-mode the form schema has `.default(…)`
        // on every section so the values we just `reset(...)` are valid by
        // construction. Calling `handleSubmit(merged)` directly avoids a
        // redundant validation round-trip and a microtask gap that could let
        // the edit form flash.
        const submit = handleSubmitRef.current
        if (submit) {
          await submit(merged)
        }
      }
    })
    // End-Session fallback handler — used by `ZoomPanel.onCreateProgressNote`
    // when the AI scribe did NOT return a draft (session lost, scribe
    // disabled, generate-draft failed). The doctor's intent is still "create
    // an unlocked Progress Note from this call", so we trigger the same
    // submit path the AI auto-submit uses against whatever the form currently
    // holds (carry-forward chart + any manual edits). On success the form
    // navigates to the new DocRef's viewer; on failure the form's red toast
    // surfaces and the edit form stays mounted.
    bridge.registerSubmitCurrent(async () => {
      const submit = handleSubmitRef.current
      if (submit) {
        await submit(methods.getValues())
      }
    })
    // PD-1181 — persist the current draft on demand WITHOUT navigating (flush
    // the debounced autosave). The End-Session flow calls this before the
    // disclaimer modal so an AI draft is durable if the tab is then closed.
    bridge.registerFlushDraft(async () => {
      // force=true: populateFromAi's reset clears isDirty, so a non-forced flush
      // would baseline-skip the brand-new AI draft without persisting it.
      await flushPendingSaveRef.current?.(true)
    })
    return () => {
      bridge.registerPopulateFromAi(null)
      bridge.registerSubmitCurrent(null)
      bridge.registerFlushDraft(null)
    }
  }, [bridge, methods, queueAiBillingSyncs])

  // --- Hydrate from existing provenance (QR + Claim) ---
  useEffect(() => {
    let active = true

    const hydrate = async () => {
      if (!provenance || !documentReference) {
        // Create-mode (no existing DocRef).
        //
        // Before the AI overlay applies, seed the form with the patient's
        // SAFETY-CONTEXT carry-forward only: active AllergyIntolerance +
        // Condition(problem-list). Medications and vitals are intentionally
        // NOT carried forward — they're visit-specific and come from the AI
        // draft / manual entry (auto-listing the full pharmacy as "reviewed",
        // or a prior vital as if measured today, would overstate the visit).
        // So the doctor sees the patient's allergies + problem list from the
        // first render, while meds + vitals stay empty until the conversation
        // populates them.
        //
        // If a `patientId` isn't available we fall back to an empty seed
        // (e.g. legacy mount where the workspace didn't pass it). The AI
        // draft will still apply as before.
        if (aiDraftAppliedRef.current) {
          // An AI draft already flushed into the form (via the populate
          // callback running ahead of the hydrate effect). Don't wipe it
          // with a fresh carry-forward reset; just clear the encounter
          // pointer and mark hydration complete.
          if (active) {
            setEncounter(undefined)
            setHydrated(true)
          }
          return
        }

        const seed: CarryForwardSeed = {
          allergies: [],
          conditions: [],
          familyHistory: [],
        }
        // Visit-type seed: the sync `'follow-up'` literal, upgraded once the history count resolves.
        let resolvedVisitType: VisitType = initialVisitType

        if (patientId) {
          try {
            const [allergiesBundle, conditionsBundle, familyHistoryBundle, derivedVisitType] =
              await Promise.all([
                medplum.searchResources('AllergyIntolerance', {
                  patient: `Patient/${patientId}`,
                  'clinical-status': 'active',
                  // FHIR R4 search param for AllergyIntolerance is `date`
                  // (mapped to AllergyIntolerance.recordedDate). The
                  // `recorded-date` param exists on Condition but NOT on
                  // AllergyIntolerance — Medplum rejected this with 400.
                  _sort: '-date',
                  _count: '50',
                }),
                medplum.searchResources('Condition', {
                  subject: `Patient/${patientId}`,
                  'clinical-status': 'active',
                  category: 'problem-list-item',
                  // Sort most-recent first so `dedupeByCodeAndDisplay` below
                  // (in `carryForwardFromConditions`) keeps the freshest
                  // record per ICD-10 code. Without this sort, the chart's
                  // duplicate-Condition accumulation surfaces oldest-first.
                  _sort: '-recorded-date',
                  _count: '100',
                }),
                // No `clinical-status` search param exists on FamilyMemberHistory;
                // entered-in-error rows are dropped in the derivation instead.
                medplum
                  .searchResources('FamilyMemberHistory', {
                    patient: `Patient/${patientId}`,
                    _count: '200',
                  })
                  // Isolated: an FMH-only failure (e.g. an AccessPolicy missing the
                  // FamilyMemberHistory rule) must not wipe the allergy/problem seeds
                  // sharing this Promise.all.
                  .catch((): FamilyMemberHistory[] => []),
                // In this same Promise.all so it lands in the one reset() below (no extra render).
                deriveInitialVisitTypeFromHistory(medplum, patientId, encounterIdProp),
              ])

            if (!active) return

            seed.allergies = carryForwardFromAllergies(allergiesBundle)
            seed.conditions = carryForwardFromConditions(conditionsBundle)
            seed.familyHistory = carryForwardFromFamilyHistory(familyHistoryBundle)
            resolvedVisitType = derivedVisitType
          } catch (err) {
            // Non-fatal — keep empty seeds + the `'follow-up'` literal so the form still opens.
            logError('progress-note-edit', 'Failed to load carry-forward chart data', err)
          }
        }

        if (!active) return

        // If the scribe bridge already has a cached AI draft waiting to be
        // flushed (a pre-mount finalize), apply it synchronously now so we
        // don't lose values to the bridge's queueMicrotask flush racing the
        // empty reset under React 19's concurrent scheduler.
        const pendingDraft = bridge?.consumeAiDraft() ?? null
        const empty = makeEmptyProgressNoteFormValues(initialNoteDate, resolvedVisitType)
        // One starter HPI paragraph so manual entry can type immediately;
        // the AI overlay replaces the array, submit drops empty paragraphs.
        empty.summary.hpi = [{ id: crypto.randomUUID(), text: '' }]
        const seeded = seedFormWithCarryForward(empty, seed)
        if (pendingDraft) {
          // Create mode: no billing lines yet, so the AI merge has nothing to report.
          const { values: merged } = applyAiDraftToFormValues(seeded, pendingDraft.draft)
          methods.reset(merged)
          aiDraftAppliedRef.current = true
          setEncounter(undefined)
          setHydrated(true)

          // FE-4 — Honor the cached `autoSubmit` opt: fire the same submit
          // path the Save button uses, and settle the awaiting Zoom-panel
          // promise once the POST resolves so the End-Session spinner can
          // stay up until navigation fires. On failure the red toast is
          // surfaced from `handleSubmit` and the edit form stays visible.
          if (pendingDraft.opts.autoSubmit) {
            // The submit handler closure has just been created on this
            // render; the ref is assigned synchronously below `handleSubmit`'s
            // declaration, so by the time this microtask defers it the ref
            // is populated. Defer one microtask so any other hydrate-effect
            // bookkeeping (setEncounter / setHydrated) commits first.
            queueMicrotask(() => {
              const submit = handleSubmitRef.current
              if (!submit) {
                pendingDraft.resolve()
                return
              }
              submit(merged).then(pendingDraft.resolve, pendingDraft.reject)
            })
          } else {
            pendingDraft.resolve()
          }
        } else if (aiDraftAppliedRef.current) {
          // Re-check after the carry-forward awaits: the populate callback can
          // apply (and even persist) an AI draft WHILE the fetch above is in
          // flight — the mount-time guard alone misses that window, and the
          // seeded reset below would wipe the applied draft (and the autosave
          // would then persist the wiped form over the rich note).
          setEncounter(undefined)
          setHydrated(true)
        } else {
          methods.reset(seeded)
          setEncounter(undefined)
          setHydrated(true)
        }
        return
      }

      const questionnaireRef = getProvenanceTargetRef(provenance, 'QuestionnaireResponse')
      const questionnaireId = questionnaireRef?.split('/')[1]
      const encounterRefForLookup = getEncounterRef(provenance)
      const encounterIdForLookup = encounterRefForLookup?.split('/')[1]

      // Encounter drives the Session Duration card and the live counts in
      // `<NoteStatsStrip>`. Read failure is non-fatal — the strip falls back
      // to the RHF form state's structured arrays.
      if (encounterIdForLookup) {
        try {
          const enc = await medplum.readResource('Encounter', encounterIdForLookup)
          if (active) setEncounter(enc)
        } catch (err) {
          logError('progress-note-edit', 'Failed to load encounter for stats strip', err)
        }
      }

      let qrPayload: QuestionnaireResponse | undefined
      if (questionnaireId) {
        try {
          qrPayload = await medplum.readResource('QuestionnaireResponse', questionnaireId)
        } catch (err) {
          logError('progress-note-edit', 'Failed to load questionnaire response', err)
        }
      }

      // The visit's billing as every surface reads it: lines off the Claim with their
      // diagnoses, the AI's suggestion merged with what the Claim bills.
      let visitBilling: Awaited<ReturnType<OrpcClient['visitBillingGet']>> | undefined
      if (encounterIdForLookup) {
        try {
          visitBilling = await queryClient.fetchQuery(
            orpc.visitBillingGet.queryOptions({ input: { encounterId: encounterIdForLookup } }),
          )
        } catch (err) {
          logError('progress-note-edit', 'Failed to load visit billing for progress note', err)
        }
      }

      if (!active) return

      // Hydrate narratives + every structured array blob-first (QR fallback):
      // the blob is on the readable DocRef, so re-open survives when an
      // org-scoped practitioner can't read the draft QR.
      const scribeDraft = readScribeDraftPayload(documentReference)

      const {
        examReason,
        prob,
        objective,
        assessment: assessmentNarrative,
        plan,
        mdm,
      } = scribeDraft
        ? hydrateSummaryFromScribeDraft(scribeDraft.soapNote)
        : {
            examReason: getQuestionnaireStringAnswer(qrPayload, 'examReason'),
            prob: getQuestionnaireStringAnswer(qrPayload, 'prob'),
            objective: getQuestionnaireStringAnswer(qrPayload, 'objective'),
            assessment: getQuestionnaireStringAnswer(qrPayload, 'assessment'),
            plan: getQuestionnaireStringAnswer(qrPayload, 'plan'),
            // No QR slot — MDM only ever existed on the blob (PD-1321).
            mdm: '',
          }

      existingSoapSnapshotRef.current = buildExistingSoapSnapshot(
        { examReason, prob, objective, assessment: assessmentNarrative, plan },
        scribeDraft?.soapNote,
      )

      // The QR hydrate is lossy (hard-codes dose/status/clinicalStatus/reaction/intent); the blob preserves them.
      let hydratedConditions = scribeDraft
        ? hydrateConditionsFromScribeDraft(scribeDraft.soapNote)
        : hydrateConditionsFromQr(qrPayload)
      const hydratedMedications = scribeDraft
        ? hydrateMedicationsFromScribeDraft(scribeDraft.soapNote)
        : hydrateMedicationsFromQr(qrPayload)
      const hydratedProcedures = scribeDraft
        ? hydrateProceduresFromScribeDraft(scribeDraft.soapNote)
        : hydrateProceduresFromQr(qrPayload)
      const hydratedServiceRequests = scribeDraft
        ? hydrateServiceRequestsFromScribeDraft(scribeDraft.soapNote)
        : hydrateServiceRequestsFromQr(qrPayload)
      let hydratedAllergies = scribeDraft
        ? hydrateAllergiesFromScribeDraft(scribeDraft.soapNote)
        : hydrateAllergiesFromQr(qrPayload)
      // Backfill pre-flag legacy blobs (`external === undefined`) and
      // shipped-era blobs whose external rows predate `chartId` (PD-1141).
      if (
        patientId &&
        hydratedAllergies.some((a) => a.external === undefined || (a.external && !a.chartId))
      ) {
        try {
          const chartAllergies = await medplum.searchResources('AllergyIntolerance', {
            patient: `Patient/${patientId}`,
            // Match the carry-forward set (active only) so a re-captured
            // inactive/resolved allergy isn't wrongly frozen read-only.
            'clinical-status': 'active',
            _count: '100',
          })
          if (!active) return
          hydratedAllergies = backfillAllergyExternalFlags(hydratedAllergies, chartAllergies)
        } catch (err) {
          // Non-fatal — rows stay editable this session; re-checked next open.
          logError('progress-note-edit', 'Failed to backfill allergy sources', err)
        }
      }
      // Same backfill for pre-flag condition rows (blobs saved before the
      // condition `external` flag existed).
      if (patientId && hydratedConditions.some((c) => c.external === undefined)) {
        try {
          const chartConditions = await medplum.searchResources('Condition', {
            subject: `Patient/${patientId}`,
            // Match the carry-forward set (active problem-list only).
            'clinical-status': 'active',
            category: 'problem-list-item',
            _count: '100',
          })
          if (!active) return
          hydratedConditions = backfillConditionExternalFlags(hydratedConditions, chartConditions)
        } catch (err) {
          // Non-fatal — rows stay editable this session; re-checked next open.
          logError('progress-note-edit', 'Failed to backfill condition sources', err)
        }
      }
      // Blob-only (like the read view): legacy QR notes keep follow-ups as
      // bullets inside `plan` — a QR fallback would re-fold them on save.
      const hydratedFollowUps = scribeDraft
        ? hydrateFollowUpsFromScribeDraft(scribeDraft.soapNote)
        : []
      // Blob-only, mirroring the read view (structured ROS never reaches the QR).
      const hydratedRos = scribeDraft ? hydrateRosFromScribeDraft(scribeDraft.soapNote) : []
      // Blob-only (PD-1377): chart-derived rows persisted at the last save,
      // minus whatever the doctor removed from the note.
      const hydratedFamilyHistory = scribeDraft
        ? hydrateFamilyHistoryFromScribeDraft(scribeDraft.soapNote)
        : []
      const vitalObservations = scribeDraft
        ? hydrateVitalObservationsFromScribeDraft(scribeDraft.soapNote)
        : hydrateVitalObservationsFromQr(qrPayload)
      const examNarrative = scribeDraft
        ? hydrateExamNarrativeFromScribeDraft(scribeDraft.soapNote)
        : objective
      const hydratedObservations: ObservationItem[] = [
        ...vitalObservations,
        ...buildExamNarrativeRow(examNarrative),
      ]
      // Codes from the Claim, AI context from the DocRef extension — the same
      // projection the read view and the Billing page use, so opening the editor
      // can never show a different code than the note above it.
      const hydratedBillingSuggestion = toFormBillingSuggestion(visitBilling?.billingSuggestion)

      // Blob-first visit type: the lossless draft blob carries the exact enum,
      // so a doctor's dropdown override (PD-1288) survives reopen even though
      // the DocRef-title fallback is a substring parse.
      const hydratedVisitType = scribeDraft?.soapNote.visitType ?? initialVisitType
      const emptyForm = makeEmptyProgressNoteFormValues(initialNoteDate, hydratedVisitType)
      // Total Time rows live on the blob, not the billing extension, so they are
      // overlaid onto whichever suggestion the extension yielded (PD-1321).
      const hydratedTimeEntries = scribeDraft
        ? hydrateTimeEntriesFromScribeDraft(scribeDraft.soapNote)
        : { providerTimeEntries: [], careCoordinatorTimeEntries: [] }
      // Mirror the create-mode re-check: an AI apply (menu action or the
      // PD-1330 first-open auto-apply) can land while the awaits above are in
      // flight — resetting now would wipe the applied draft and the autosave
      // would persist the wiped form over the rich note.
      if (aiDraftAppliedRef.current) {
        setHydrated(true)
        return
      }
      methods.reset({
        ...emptyForm,
        noteDate: initialNoteDate,
        visitType: hydratedVisitType,
        aiAssisted: scribeDraft?.soapNote.aiAssisted === true,
        encounterLanguage: scribeDraft?.soapNote.encounterLanguage,
        summary: {
          chiefComplaint: examReason,
          hpi: splitHpiParagraphs(prob),
          ros: hydratedRos,
          assessment: assessmentNarrative,
          plan,
          mdm,
        },
        conditions: hydratedConditions,
        procedures: hydratedProcedures,
        medications: hydratedMedications,
        serviceRequests: hydratedServiceRequests,
        allergies: hydratedAllergies,
        familyHistory: hydratedFamilyHistory,
        observations: hydratedObservations,
        followUps: hydratedFollowUps,
        billingSuggestion: {
          ...(hydratedBillingSuggestion ?? emptyForm.billingSuggestion),
          ...hydratedTimeEntries,
        },
        // Billing entries: the Claim's lines as the server reads them (the note's
        // blob only backfills a line's diagnoses there). The blob is the fallback
        // only when that read failed — an autosave with no lines would otherwise
        // empty the visit's Claim.
        billingEntries: visitBilling
          ? toBillingEntries(visitBilling.entries)
          : scribeDraft
            ? hydrateBillingEntriesFromScribeDraft(scribeDraft.soapNote)
            : [],
      })
      billingIncompleteRef.current = hasUnavailableDiagnoses(visitBilling)
      // Edit-mode hydrate complete — surface the editable form.
      setHydrated(true)
    }

    void hydrate()

    return () => {
      active = false
    }
  }, [
    medplum,
    provenance,
    documentReference,
    methods,
    initialNoteDate,
    initialVisitType,
    bridge,
    patientId,
    encounterIdProp,
    queryClient,
    orpc,
  ])

  // Fresh notes send noteDate only when the doctor edited it — otherwise the
  // BE anchors scribe saves to the visit date (the form default is mount time).
  const stripUntouchedNoteDate = (soapNote: SoapNoteDto) => {
    if (!documentReference && !methods.getFieldState('noteDate').isDirty) {
      soapNote.noteDate = undefined
    }
  }

  const handleSubmit = async (values: ProgressNoteEditFormValues) => {
    try {
      setSubmitting(true)

      // Snapshot the practitioner profile + project + org for the visit
      // context the middleware expects. In create-mode the workspace can pass
      // these in directly; in edit-mode we resolve them from Medplum like
      // we've always done.
      const profile = medplum.getProfile()
      if (!profile?.id) {
        throw new Error('Practitioner profile is required to submit a progress note.')
      }
      const projectId = projectIdProp ?? medplum.getProject()?.id
      if (!projectId) {
        throw new Error('Project not found')
      }
      if (!patientId) {
        throw new Error('Patient id not found')
      }

      let organizationId = organizationIdProp
      if (!organizationId) {
        const practitionerRole = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        organizationId = practitionerRole?.organization?.reference?.split('/')[1]
      }
      if (!organizationId) {
        throw new Error('Organization not found for practitioner')
      }

      const soapNote = draftToSoap(values, existingSoapSnapshotRef.current, priceFor, refusedFor)
      stripUntouchedNoteDate(soapNote)
      // PD-1804: once a payload takes its ROS rows from the form, stop carrying the
      // hidden rows now, so a save built while this one is in flight can't resend them.
      existingSoapSnapshotRef.current = refreshExistingSoapSnapshot(
        existingSoapSnapshotRef.current,
        soapNote,
      )
      // FE-4 — `scribeSessionId` is forwarded only when a scribe session is
      // active (i.e. the doctor just finalized via "Create Progress Note"
      // and the in-memory id has not been cleared yet). The middleware uses
      // it to attach the `Device/scribe-ai-v1` Provenance agent for the
      // indigo "AI" pill on the Progress Notes list. Manual saves (no scribe)
      // omit the field — the BE handles `undefined` the same as a missing
      // key.
      const visitContext: Parameters<typeof client.visitSaveSoapNoteDraft>[0]['visitContext'] = {
        patientId,
        practitionerId: practitionerIdProp ?? profile.id,
        projectId,
        organizationId,
        ...(scribeSessionId ? { scribeSessionId } : {}),
      }

      // The explicit Save persists the same `SoapNoteDto` the auto-save would;
      // record it as the baseline so the debounced auto-save doesn't
      // immediately re-fire for the just-saved content.
      lastSavedSnapshotRef.current = JSON.stringify(soapNote)

      // FE-2 fix — the BE soap-note endpoints now return
      // `CreateSoapNoteResponseDto = { documentReferenceId, encounterId,
      // provenanceId? }` so the FE can navigate the doctor straight to the
      // locked viewer URL after save. The typed oRPC `client` infers the
      // response type directly from the contract, so no cast is needed.
      //
      // Branch by mode:
      //  - Edit-mode (existing DocRef + Provenance) → save-draft against the
      //    encounter-scoped endpoint that overwrites the existing chain. We must
      //    resolve `encounterId` from the Provenance.
      //  - Create-mode with an `encounterId` prop (call page or side-panel
      //    flow that pre-materialized the Encounter) → save-draft against the
      //    encounter-scoped endpoint so the existing Encounter receives the cascade.
      //  - Create-mode WITHOUT an `encounterId` (legacy fallback) → submit to
      //    `/visits/soap-note` which creates a fresh Encounter +
      //    DocumentReference cascade implicitly.
      type SoapNoteCreateResponse = Awaited<ReturnType<typeof client.visitSaveSoapNoteDraft>>
      // draftToSoap yields the legacy `SoapNoteDto` (billingItems.dx optional);
      // the oRPC contract requires `dx`, which draftToSoap always populates.
      const soapNoteInput = (
        billingIncompleteRef.current ? { ...soapNote, billing: undefined } : soapNote
      ) as Parameters<typeof client.visitSaveSoapNoteDraft>[0]['soapNote']
      let response: SoapNoteCreateResponse
      if (provenance && documentReference?.id) {
        const encounterRef = getEncounterRef(provenance)
        if (!encounterRef) {
          throw new Error('Encounter not found on provenance')
        }
        const encounterId = encounterRef.split('/')[1]
        response = await client.visitSaveSoapNoteDraft({
          encounterId,
          soapNote: soapNoteInput,
          visitContext,
          ...(noteIdRef.current ? { documentReferenceId: noteIdRef.current } : {}),
        })
      } else if (encounterIdProp) {
        response = await client.visitSaveSoapNoteDraft({
          encounterId: encounterIdProp,
          soapNote: soapNoteInput,
          visitContext,
          ...(noteIdRef.current ? { documentReferenceId: noteIdRef.current } : {}),
        })
      } else {
        response = await client.visitSubmitSoapNote({
          soapNote: soapNote as Parameters<typeof client.visitSubmitSoapNote>[0]['soapNote'],
          visitContext,
        })
      }
      // Bust the Medplum SDK's in-memory search cache (LayoutContext.tsx wires
      // `cacheTime: 5000`) for the resource types whose stale Bundles would
      // otherwise hide the just-written cascade:
      //   - `DocumentReference` → drives the Progress Notes left-rail list in
      //     <ListLayout>; without this the rail returns the cached pre-save
      //     Bundle and the new note appears invisible until a hard refresh.
      //   - `Provenance` → drives the indigo "AI" pill on each rail row via
      //     `_revinclude=Provenance:target`. Even if the DocRef refreshes, the
      //     pill wouldn't resolve without a fresh Provenance Bundle.
      //   - `Encounter` → the in-progress visit is referenced by the rail's
      //     downstream chart-pane viewer; matches the pattern at
      //     Schedule/service.ts:248 / Home/CalendarCmp.tsx:207.
      // Must run BEFORE `onSave?.()` / `onFinish?.()` so the
      // `progressNotes:refresh` event emitted by parents (PatientPage.tsx:248,
      // PatientChartSidePanelHost.tsx:165) re-runs <ListLayout>'s effect
      // against a cleared cache.
      noteIdRef.current = response.documentReferenceId
      medplum.invalidateSearches('DocumentReference')
      medplum.invalidateSearches('Provenance')
      medplum.invalidateSearches('Encounter')

      // FE-2 fix — also invalidate the TanStack-Query-cached chart-section
      // counts so the patient-chart sidebar's "Progress Notes" badge ticks
      // up immediately. Prior art at `useLockAndTransmit.ts:67`. The
      // canonical key shape `['patient-chart-counts', { patientId }]` lives
      // at `hooks/patientChartCountsQueryKey.ts`.
      if (patientId) {
        queryClient.invalidateQueries({ queryKey: patientChartCountsQueryKey(patientId) })
      }

      showNotification({
        color: 'green',
        message: isCreateMode ? 'Progress note saved' : 'Progress note updated',
      })
      onSave?.()
      onFinish?.()

      // FE-2 fix — navigate the doctor to the freshly-saved Progress Note in
      // the read-only viewer so they land on what they just wrote, not on
      // Demographics (the previous behavior, which `onFinish` left them on
      // because it only flipped a local panel-open boolean).
      //
      // The route pattern matches `ListLayout.tsx:190-199` + `Documents.tsx:142-146`
      // expectations: `params['*']` is split on `/` and index 0 is the
      // DocumentReference id, index 1 is the dataKey (`progress-notes`).
      //
      // CALL-PAGE BRANCH: when `encounterIdProp` is truthy the call route is
      // still mounted (`/call/:encounterId/*`). We navigate to the patient
      // route — the call is over by the time the doctor saves the SOAP
      // (EndSessionPrompt has already torn down the scribe session), and the
      // patient route's chart viewer + sidebar are the canonical post-save
      // destination. The call route will unmount as part of the navigation.
      // SIDE-PANEL BRANCH (no `encounterIdProp` OR `encounterIdProp` came
      // from the side-panel's pre-materialized Encounter): same target.
      if (patientId && response?.documentReferenceId) {
        // The AI disclaimer is now a gate on the on-call finalize flow (shown
        // before the note opens), so this navigate no longer carries it.
        navigate(`/patients/${patientId}/${response.documentReferenceId}/progress-notes`)
      }
    } catch (error) {
      logError('progress-note-edit', 'Failed to save progress note', error)
      // PD-1322 (S24d) — a signed-note 409 is unreachable through the UI (a
      // final note offers "Add addendum", never Edit), so reaching here means a
      // genuine race: the note was locked between this form opening and the
      // save landing. Point at the addendum flow instead of the raw conflict.
      if (isSignedNoteConflict(error)) {
        showNotification({
          color: 'yellow',
          title: SIGNED_NOTE_CONFLICT_TITLE,
          message: SIGNED_NOTE_CONFLICT_MESSAGE,
        })
      } else {
        showNotification({ color: 'red', message: normalizeErrorString(error) })
      }
      // PD-1440 — on any conflict also let the parent close the editor.
      if (isConflictError(error)) onSaveConflict?.()
    } finally {
      setSubmitting(false)
    }
  }

  const onConfirmSubmit = methods.handleSubmit((values) => {
    // Mockup parity (ui-designs/.../ChartPage.tsx:3772-3783) — Save fires
    // directly without an overwrite-confirm modal. The doctor already sees an
    // "Editing Progress Note" bar at the top with explicit Cancel/Save, so a
    // second confirmation step adds friction without protecting against a
    // realistic mistake.
    void handleSubmit(values)
  })

  // FE-4 — keep the auto-submit ref pointed at the latest `handleSubmit`
  // closure so the bridge's populate callback (registered once at mount) can
  // always reach the fresh function with the current props (patientId,
  // encounterId, practitionerId, …). Without this, the populate callback
  // would invoke a stale handler captured on first registration and submit
  // with the wrong visit context.
  handleSubmitRef.current = handleSubmit

  // ---------------------------------------------------------------------------
  // Draft auto-save (debounced) — gate-clinical-writes-until-Lock.
  //
  // While editing a PRELIMINARY (unlocked) note, persist the draft ~1.6s after
  // the doctor stops typing. The save path is the SAME idempotent save-draft
  // the explicit Save uses (`draftToSoap(...)` → POST
  // `/visits/:encounterId/soap-note`), which now writes ONLY the draft
  // (QuestionnaireResponse + a single find-or-update preliminary
  // DocumentReference) and NO clinical resources. Auto-save NEVER navigates and
  // NEVER fires for a `final` note.
  //
  // A stable `encounterId` is required: edit-mode resolves it from the
  // Provenance; create-mode uses the `encounterId` prop (call page / pre-
  // materialized side-panel encounter). The legacy create-mode path WITHOUT an
  // encounter (`POST /visits/soap-note`) mints a NEW encounter on every call,
  // so it is intentionally excluded from auto-save — that flow saves only on
  // the explicit Save / AI auto-submit.
  // ---------------------------------------------------------------------------
  const docStatus = documentReference?.docStatus
  // `final` notes are read-only — never auto-save them. Create-mode (no doc)
  // and `preliminary` notes are eligible.
  const isLocked = docStatus === 'final'
  const autoSaveEncounterId = isCreateMode
    ? encounterIdProp
    : provenance
      ? getEncounterRef(provenance)?.split('/')[1]
      : undefined

  // The visit's billing re-read after a save from the billing summary or its editor:
  // the form's copy follows the server so the next autosave resends what is billed,
  // not what was. Total Time rows stay as the form has them, and unsaved Problem List
  // syncs stay on top of the server's lines.
  const visitBillingQuery = useQuery(
    orpc.visitBillingGet.queryOptions({
      input: { encounterId: autoSaveEncounterId ?? '' },
      enabled: hydrated && Boolean(autoSaveEncounterId),
    }),
  )
  useEffect(() => {
    const visitBilling = visitBillingQuery.data
    if (!hydrated || !visitBilling) return

    billingIncompleteRef.current = hasUnavailableDiagnoses(visitBilling)
    const suggestion = toFormBillingSuggestion(visitBilling.billingSuggestion)
    if (suggestion) {
      const current = methods.getValues('billingSuggestion')
      methods.setValue('billingSuggestion', {
        ...suggestion,
        providerTimeEntries: current.providerTimeEntries,
        careCoordinatorTimeEntries: current.careCoordinatorTimeEntries,
      })
    }
    const selectedCptCode = getSelectedCandidate(methods.getValues('billingSuggestion'))?.code
    methods.setValue(
      'billingEntries',
      pendingDiagnosisSyncsRef.current.reduce(
        (entries, { change }) => applyNoteDiagnosisChange(entries, change, selectedCptCode).entries,
        toBillingEntries(visitBilling.entries),
      ),
    )
  }, [visitBillingQuery.data, hydrated, methods])

  // Holds the latest flush closure so the unmount cleanup (mount-once effect,
  // empty deps) can reach the current form state without re-subscribing.
  const flushPendingSaveRef = useRef<((force?: boolean) => Promise<boolean>) | null>(null)

  useEffect(() => {
    autoSaveActiveRef.current = true
    return () => {
      // Best-effort flush on unmount — covers exit paths that DON'T go through
      // the Cancel/Save buttons (e.g. the doctor navigates to another note or
      // route while mid-edit). Runs BEFORE we flip `autoSaveActiveRef` so the
      // save core doesn't early-return. We can't await inside a cleanup, so
      // this can't block; the BLOCKING guarantee lives in the Cancel handler
      // (`handleCancel`) and the explicit Save, which are the deliberate
      // "leave the edit form" gestures. A failure here is logged by
      // `performAutoSave`.
      void flushPendingSaveRef.current?.()
      autoSaveActiveRef.current = false
    }
  }, [])

  // The awaitable draft-save core. Returns:
  //   - `true`  → persisted OR intentionally skipped (nothing dirty / no-op);
  //               either way the server holds the latest content.
  //   - `false` → the POST threw; the server does NOT have these edits.
  // Both the debounced auto-save AND the flush-on-exit path call this so there
  // is exactly one save translation (`draftToSoap` → save-draft).
  const performAutoSave = async (force = false): Promise<boolean> => {
    // Re-check guards at fire time (props/state may have changed during the
    // debounce window).
    if (!autoSaveActiveRef.current) return true
    if (isLocked || !canEdit) return true
    if (!hydrated) return true
    if (!autoSaveEncounterId || !patientId) return true
    // Don't auto-save while an explicit submit is mid-flight — that path owns
    // the snapshot baseline + navigation.
    if (submitting) return true
    // PD-1516 — no saves during an active dictation: the provider must finish
    // or cancel the recording first (the preview is never note content).
    if (dictate.activeRef.current) return true

    const syncedThrough = diagnosisSyncSeqRef.current
    const settleDiagnosisSyncs = () => {
      pendingDiagnosisSyncsRef.current = pendingDiagnosisSyncsRef.current.filter(
        (sync) => sync.seq > syncedThrough,
      )
    }
    const soapNote = draftToSoap(
      methods.getValues(),
      existingSoapSnapshotRef.current,
      priceFor,
      refusedFor,
    )
    stripUntouchedNoteDate(soapNote)
    // PD-1804: once a payload takes its ROS rows from the form, stop carrying the
    // hidden rows now, so a save built while this one is in flight can't resend them.
    existingSoapSnapshotRef.current = refreshExistingSoapSnapshot(
      existingSoapSnapshotRef.current,
      soapNote,
    )
    const snapshot = JSON.stringify(soapNote)
    // Nothing changed since the last persisted draft — skip the network write.
    if (snapshot === lastSavedSnapshotRef.current) {
      settleDiagnosisSyncs()
      return true
    }
    // First fire after hydrate: the watch can trigger from the hydrate/AI
    // `methods.reset(...)` itself, before the doctor has touched anything.
    // Establish the baseline silently (no POST) unless the form is genuinely
    // dirty — this enforces "don't auto-save when nothing changed".
    // `force` (PD-1181 pre-modal flush) bypasses this: populateFromAi's
    // `reset(...)` clears isDirty, so without force the brand-new AI draft would
    // be silently baselined and never POSTed.
    if (!force && lastSavedSnapshotRef.current === null && !methods.formState.isDirty) {
      lastSavedSnapshotRef.current = snapshot
      return true
    }

    setAutoSaveStatus('saving')
    try {
      const saved = await saveProgressNoteDraft({
        encounterId: autoSaveEncounterId,
        patientId,
        soapNote,
        withholdBilling: billingIncompleteRef.current,
        scribeSessionId,
        documentReferenceId: noteIdRef.current,
        practitionerId: practitionerIdProp,
        organizationId: organizationIdProp,
        projectId: projectIdProp,
      })
      noteIdRef.current = saved.documentReferenceId
      // Record the persisted snapshot so subsequent debounced ticks no-op until
      // the doctor edits again. Guard the status setState against unmount, but
      // still report success to the caller — the server DID receive the write.
      lastSavedSnapshotRef.current = snapshot
      settleDiagnosisSyncs()
      if (autoSaveActiveRef.current) setAutoSaveStatus('saved')
      // The billing summary reads the visit's billing from the server, and this save
      // just rewrote the Claim and the note's suggestion — let it re-read.
      void queryClient.invalidateQueries({
        queryKey: orpc.visitBillingGet.key({ input: { encounterId: autoSaveEncounterId } }),
      })
      return true
    } catch (error) {
      // Background auto-save is best-effort — surface the failure quietly (no
      // blocking toast here; the exit flush in `flushPendingSave` is the one
      // that hard-blocks the path to Lock). Reset the indicator so a
      // subsequent edit re-attempts.
      logError('progress-note-edit', 'Draft auto-save failed', error)
      if (autoSaveActiveRef.current) setAutoSaveStatus('idle')
      // PD-1440 — a 409 is NOT transient (every retry will be refused): tell the
      // parent so it closes this editor instead of leaving a dead form.
      if (isConflictError(error)) onSaveConflict?.()
      return false
    }
  }
  // Keep the ref pointed at the latest closure so flush-on-exit reads current
  // form state + props, not a snapshot captured on first render.
  performAutoSaveRef.current = performAutoSave

  const runAutoSave = useDebouncedCallback(async () => {
    await performAutoSaveRef.current?.()
  }, 1600)

  // PD-1597 — the AI draft's autosave can beat the charge quote; once the quote
  // lands the serialized prices change, so re-run the (snapshot-guarded) save.
  useEffect(() => {
    if (chargeQuote) runAutoSave()
  }, [chargeQuote, runAutoSave])

  // Subscribe to form changes and schedule a debounced auto-save. RHF's
  // `methods.watch(cb)` fires on every field change; the debounce collapses a
  // burst of keystrokes into a single save ~1.6s after the last edit. The
  // change-detection (snapshot compare) lives inside `performAutoSave` so a
  // `methods.reset(...)` that doesn't alter content (e.g. the hydrate/AI merge)
  // doesn't cause a redundant POST.
  useEffect(() => {
    if (isLocked) return
    const subscription = methods.watch(() => {
      runAutoSave()
    })
    return () => subscription.unsubscribe()
  }, [methods, runAutoSave, isLocked])

  // ---------------------------------------------------------------------------
  // Flush-on-exit + block-on-fail (Option A — the lock-from-viewer guarantee).
  //
  // Lock no longer force-saves the on-screen note; it materializes from the
  // server-stored draft blob. So the moment the doctor LEAVES the edit form
  // (Cancel, or any unmount) we must guarantee the latest edits are in that
  // blob BEFORE the read-only `<ProgressNoteViewer>` (and its Lock button)
  // appears.
  //
  // `flushPendingSave` cancels any debounced tick, runs the SAME save core
  // synchronously, and AWAITS it. It returns `false` only when a genuine,
  // not-yet-persisted change failed to save — the Cancel handler uses that to
  // BLOCK the exit so a failed save can never lead to locking stale content.
  // ---------------------------------------------------------------------------
  const flushPendingSave = async (force = false): Promise<boolean> => {
    // Cancel the pending debounce so we don't double-fire; we run the save
    // ourselves below and await it.
    runAutoSave.cancel()
    return (await performAutoSaveRef.current?.(force)) ?? true
  }
  flushPendingSaveRef.current = flushPendingSave

  // PD-2016 — a Problem List edit reaches the visit's billing lines while the note is open.
  // It saves through the debounce like any edit: an immediate flush per edit ran
  // overlapping saves, and the older one could land last. Until a save carries it, the
  // change is queued for the billing re-read to replay. Edit billing reads the server
  // Claim, so it flushes and re-reads once before opening (`openBillingEditor`).
  const handleDiagnosisChange = (change: NoteDiagnosisChange) => {
    if (!canEdit || isLocked) return
    // PD-1958 withhold: saves leave billing out, so a sync would never reach the Claim.
    if (billingIncompleteRef.current) {
      showNotification({ color: 'yellow', message: BILLING_WITHHELD_NOTICE })
      return
    }
    const current = methods.getValues('billingEntries')
    const { entries, skipped } = applyNoteDiagnosisChange(
      current,
      change,
      getSelectedCandidate(methods.getValues('billingSuggestion'))?.code,
    )
    if (skipped) {
      const code =
        change.kind === 'add' ? change.dx.code : change.kind === 'rename' ? change.to.code : ''
      showNotification({
        color: 'yellow',
        message: capSkippedNotice([code ?? ''], skipped.procedureCode),
      })
      return
    }
    if (entries === current) return
    queueDiagnosisSync(change)
    methods.setValue('billingEntries', entries, { shouldDirty: true })
    runAutoSave()
  }

  // Opening with queued syncs still unsaved (failed save, or a save skipped during
  // AI Dictate) would let them replay over the coder's correction on the next
  // billing re-read, so the editor stays closed until they settle.
  const openBillingEditor = async (): Promise<boolean> => {
    const saved = await flushPendingSave()
    if (!saved) {
      showNotification({
        color: 'red',
        title: "Couldn't save your changes",
        message:
          "Edit billing didn't open because the note couldn't be saved. Click it again to retry.",
      })
      return false
    }
    if (pendingDiagnosisSyncsRef.current.length > 0) {
      showNotification({
        color: 'yellow',
        title: 'Edit billing is waiting on AI Dictate',
        message: 'Finish or discard the AI Dictate preview, then open Edit billing.',
      })
      return false
    }
    if (!autoSaveEncounterId) return true
    await queryClient.refetchQueries(
      { queryKey: orpc.visitBillingGet.key({ input: { encounterId: autoSaveEncounterId } }) },
      { cancelRefetch: false },
    )
    return true
  }

  // --- AI-draft action handlers ---------------------------------------------
  // Both actions land content through the SAME pipeline as the in-call
  // populate: PD-1110 disclaimer → `applyAiDraftToFormValues` merge →
  // `methods.reset(merged)` → FORCED flush (the reset clears isDirty, so an
  // unforced flush would baseline-skip the new content without persisting it).
  const applyAiResultToForm = async (result: FinalizeScribeResult): Promise<boolean> => {
    const prepared = toStructuredDraft(result)
    if (!prepared.hasAiSignal) {
      throw new Error('AI draft has no content')
    }
    await confirmDraftDisclaimer(result, prepared.hasAiSignal)
    const { values: merged, billingSync } = applyAiDraftToFormValues(
      methods.getValues(),
      prepared.draft,
    )
    queueAiBillingSyncs(billingSync)
    methods.reset(merged)
    aiDraftAppliedRef.current = true
    notifyAiBillingSync(billingSync, billingIncompleteRef.current)
    const persisted = await flushPendingSave(true)
    if (!persisted) {
      showNotification({
        color: 'red',
        title: "Couldn't save the updated note",
        message:
          'The content is in the form but not persisted yet. Edit any field or press Save to retry.',
      })
    }
    return persisted
  }

  // PD-1516 flow (b) — whole-note dictation runs the SAME pipeline as a visit
  // draft (chart facts, validation, billing) and lands through the same
  // disclaimer-gated apply, so the provider reviews it in the editor before
  // anything is signed. Ref assignment: the dictate hook mounts far above.
  organizeDictationImplRef.current = async (dictation: string, isStale: () => boolean) => {
    if (!aiActionsEncounterId || aiActionInFlightRef.current) return false
    aiActionInFlightRef.current = true
    try {
      setAiAction('regenerate')
      // Re-dictation merges, never overwrites (PD-1516):
      // hand the pipeline the note as it stands so provider content survives.
      // A reopened note is provider content even before its first edit; a
      // virgin create-mode form only holds the chart carry-forward seed, which
      // must not masquerade as a provider note.
      const existingNote =
        !isCreateMode || aiDraftAppliedRef.current || methods.formState.isDirty
          ? serializeNoteForMerge(methods.getValues())
          : null
      const result = await generateFromDictation(
        aiActionsEncounterId,
        dictation,
        existingNote ?? undefined,
      )
      // The provider started a newer dictation while this one generated —
      // discard it here, BEFORE the disclaimer and the form merge.
      if (isStale()) return true
      // The provider left the note while this generated. The server owns note
      // creation on this path (PD-1577); this form is gone, so the disclaimer
      // would open over whatever page they moved to and the flush behind it
      // would no-op. `autoSaveActiveRef` is the unmount signal (cleared in cleanup).
      if (!autoSaveActiveRef.current) return true
      await applyAiResultToForm(result)
      return true
    } catch (error) {
      logError('progress-note-edit', 'Dictation draft generation failed', error)
      // `useFinalizeScribe` already toasts its own generation failures; only
      // surface the ones it can't have (empty result, disclaimer/merge step).
      if (!(error instanceof ScribeDraftGenerationError)) {
        showNotification({
          color: 'red',
          title: "Couldn't organize the dictation",
          message: 'The dictated note could not be generated. Try again.',
        })
      }
      return false
    } finally {
      aiActionInFlightRef.current = false
      setAiAction(null)
    }
  }

  // PD-1330 — first open of an auto-created draft: apply the stored draft
  // through the normal disclaimer + merge + forced flush (which clears the
  // unreviewed tag server-side). Ref-gated against the async artifact
  // resolution double-firing; no extra "replace content?" confirm — the note
  // body IS the draft's own narrative skeleton.
  const autoAppliedRef = useRef(false)
  useEffect(() => {
    if (!autoApplyAiDraft || autoAppliedRef.current) return
    // Wait for the edit-mode hydrate: applying earlier lets the hydrate's
    // reset wipe the applied draft, and the forced flush would no-op.
    if (!hydrated) return
    // The artifact can be genuinely gone (best-effort persist, or superseded
    // by a re-finalized transcript). Opening silently would let the doctor
    // sign a narrative-only note believing it was reviewed — say so instead.
    if (scribeArtifactResolveState !== 'loading' && !aiDraftDocRefId) {
      autoAppliedRef.current = true
      showNotification({
        color: 'orange',
        title: 'AI draft unavailable',
        message:
          'The stored AI draft could not be loaded — this note contains unreviewed AI narrative. Review carefully before signing.',
      })
      return
    }
    if (!aiDraftDocRefId || aiActionInFlightRef.current) return
    autoAppliedRef.current = true
    void (async () => {
      aiActionInFlightRef.current = true
      try {
        setAiAction('load')
        const result = await loadStoredScribeDraft(medplum, aiDraftDocRefId)
        await applyAiResultToForm(result)
      } catch (error) {
        logError('progress-note-edit', 'First-open AI draft apply failed', error)
        showNotification({
          color: 'red',
          title: "Couldn't load the AI draft",
          message: 'Open the AI menu to retry, or edit the note manually.',
        })
      } finally {
        aiActionInFlightRef.current = false
        setAiAction(null)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApplyAiDraft, aiDraftDocRefId, hydrated, scribeArtifactResolveState])

  const handleLoadAiDraft = async (): Promise<void> => {
    if (!aiDraftDocRefId || aiActionInFlightRef.current) return
    aiActionInFlightRef.current = true
    try {
      // Instant only when the note is structurally blank AND untouched this
      // session. The dirty check matters for edited carry-forward CONDITION
      // rows: they keep `external: true` and (unlike allergies) carry no
      // `updated` flag, so form dirtiness is the only signal the doctor
      // changed one — and a matching AI condition would win the merge.
      const untouchedBlank = isEffectivelyBlankNote(methods.getValues()) && !formIsDirty
      if (!untouchedBlank && !(await confirmLoadAiDraft())) return
      setAiAction('load')
      const result = await loadStoredScribeDraft(medplum, aiDraftDocRefId)
      await applyAiResultToForm(result)
    } catch (error) {
      logError('progress-note-edit', 'Load stored AI draft failed', error)
      showNotification({
        color: 'red',
        title: "Couldn't load the AI draft",
        message: 'The saved draft could not be read. Try again.',
      })
    } finally {
      aiActionInFlightRef.current = false
      setAiAction(null)
    }
  }

  const handleRegenerateNote = async (): Promise<void> => {
    if (!aiTranscriptDocRefId || !aiActionsEncounterId || aiActionInFlightRef.current) return
    aiActionInFlightRef.current = true
    try {
      // Approved decision: NO regenerate ever runs without this confirmation.
      if (!(await confirmRegenerateNote())) return
      setAiAction('regenerate')
      const result = await generateFromTranscript(
        aiActionsEncounterId,
        aiTranscriptDocRefId,
        // The scratchpad survives failed generations; feed the retry the same
        // doctor observations the original attempt had (mirrors the rail).
        readObservationsFromStorage(aiActionsEncounterId),
      )
      const persisted = await applyAiResultToForm(result)
      // The observations made it into the note — drop the scratchpad so a later
      // regenerate doesn't replay stale input (mirrors the rail).
      clearObservationsFromStorage(aiActionsEncounterId)
      if (persisted) {
        // A regenerate ends in REVIEW, not more editing — land on the
        // read-only viewer of the fresh note, the same destination the
        // in-call auto-submit uses. `onSave` covers the chart edit-mode
        // toggle (Documents flips back to its viewer and remounts).
        if (onSave) {
          onSave()
        } else if (patientId && aiNoteId) {
          void navigate(`/patients/${patientId}/${aiNoteId}/progress-notes`)
        }
      }
    } catch (error) {
      logError('progress-note-edit', 'Regenerate from transcript failed', error)
      // `useFinalizeScribe` already toasts its own generation failures; only
      // surface the ones it can't have (empty result, disclaimer/merge step).
      if (!(error instanceof ScribeDraftGenerationError)) {
        showNotification({
          color: 'red',
          title: "Couldn't regenerate the note",
          message: 'The transcript is still saved. Try again, or keep editing manually.',
        })
      }
    } finally {
      aiActionInFlightRef.current = false
      setAiAction(null)
    }
  }

  /**
   * Cancel handler — the deliberate "leave the edit form" gesture. BLOCK-ON-
   * FAIL: flush any pending edits to the server FIRST and await it. Only exit
   * (call the parent's `onCancel`, which unmounts this form and remounts the
   * read-only viewer with its Lock button) once the server holds the latest
   * content. If the flush fails, surface a red toast and STAY in the edit form
   * so a failed save can never lead to locking stale / unpersisted content.
   */
  const handleCancel = async () => {
    const saved = await flushPendingSave()
    if (!saved) {
      showNotification({
        color: 'red',
        title: "Couldn't save your changes",
        message: 'Your latest edits are not saved yet. Fix the issue and try again.',
      })
      return
    }
    onCancel?.()
  }

  // FE-3 — Don't render the editable form until the hydrate effect has
  // either:
  //   (a) [create-mode] fetched the patient's carry-forward chart data and
  //       reset the form with it (so the doctor never sees an empty form
  //       flash before the carry-forward materializes), or
  //   (b) [edit-mode] read the QR + Claim and reset the form with the
  //       prior note's values.
  // The skeleton fills the same vertical footprint as the form's first
  // viewport so the layout doesn't reflow when it swaps in.
  if (!hydrated) {
    return (
      <Paper p="md" withBorder mt={flushTop ? 0 : 'lg'}>
        <Stack gap="md">
          <Skeleton height={28} width={220} />
          <Group grow>
            <Skeleton height={36} />
            <Skeleton height={36} />
          </Group>
          <Skeleton height={120} />
          <Skeleton height={160} />
          <Skeleton height={120} />
        </Stack>
      </Paper>
    )
  }

  // Read per render while an AI action is live — cheap, and the overlay is up
  // for the whole window so the card can't be seen at a stale position.
  const paperRect = aiAction ? paperRef.current?.getBoundingClientRect() : undefined

  return (
    <FormProvider {...methods}>
      <AiDictateContext.Provider value={dictate}>
        <TocPresenceProvider>
          <Paper ref={paperRef} p="md" withBorder mt={flushTop ? 0 : 'lg'} pos="relative">
            {/* Either AI action blocks the whole form: nothing may be edited
              while replacement content is being fetched or generated — a
              keystroke landing mid-flight would be merged over without the
              replace-confirm (a failed run leaves the form untouched). */}
            <LoadingOverlay
              visible={aiAction !== null}
              zIndex={30}
              overlayProps={{ blur: 1 }}
              // Dim + pointer-block only; the progress card renders in the
              // Portal below — Mantine's loader box clips/mis-anchors a card
              // taller than itself, so nothing visible lives in this slot.
              styles={{ loader: { display: 'none' } }}
            />
            {aiAction !== null && (
              <MantinePortal>
                {/* Viewport-pinned so it stays on screen however far the tall
                  Paper is scrolled; horizontally centered over the NOTE via
                  the measured rect (viewport-center lands beside the note
                  when the chart sidebar / call rail is open). Portaled to
                  <body> so no ancestor overflow or transform can clip it;
                  z 150 sits above the dim layer (30) and below modals (200),
                  keeping the PD-1110 disclaimer on top. */}
                <div
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: paperRect ? paperRect.left + paperRect.width / 2 : '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 150,
                    pointerEvents: 'none',
                  }}
                >
                  {aiAction === 'regenerate' ? (
                    // The SAME generating card the Zoom rail shows while a note
                    // is produced (§8 EndSessionPrompt), in its light variant.
                    <div className="w-[320px] rounded-[14px] border border-slate-200 bg-white px-5 py-6 shadow-xl">
                      <GeneratingProgress progress={draftProgress} variant="light" />
                    </div>
                  ) : (
                    <div className="flex w-[320px] items-center justify-center gap-2 rounded-[14px] border border-slate-200 bg-white px-5 py-4 shadow-xl">
                      <Loader size="sm" />
                      <Text size="sm" fw={600} c="#334155">
                        Reloading original note…
                      </Text>
                    </div>
                  )}
                </div>
              </MantinePortal>
            )}
            <Stack gap="md">
              {/* Mockup-parity edit-mode top action bar. Mirrors
              `ui-designs/.../ChartPage.tsx:3931-3945`: a sky-blue chip on the
              left ("Editing Progress Note") + Cancel / Save on the right.
              Sticky so the doctor always sees Save/Cancel regardless of scroll
              depth, with a 2px blue bottom-accent to separate it from the
              scrolling content (mockup uses `borderBottom: 2px solid #3b82f6`).
              Cancel renders only when an `onCancel` callback is wired in by
              the parent. */}
              <Group
                justify="space-between"
                align="center"
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 5,
                  background: '#eff6ff',
                  borderBottom: '2px solid #3b82f6',
                  padding: '8px 12px',
                  margin: '-16px -16px 8px',
                  borderRadius: '4px 4px 0 0',
                }}
              >
                <Group gap={6} align="center" wrap="nowrap" c="#1e40af">
                  <IconEdit size={14} />
                  <Text size="xs" fw={600}>
                    {isCreateMode ? 'New Progress Note' : 'Editing Progress Note'}
                  </Text>
                  {/* Subtle draft auto-save indicator. Hidden while idle so the bar
                  stays quiet; shows a spinner while a debounced save is in
                  flight and a green check once the draft is persisted. Never
                  rendered for a locked note (auto-save is disabled there). */}
                  {!isLocked && autoSaveStatus === 'saving' && (
                    <Group
                      gap={4}
                      align="center"
                      wrap="nowrap"
                      c="#64748b"
                      data-testid="autosave-saving"
                    >
                      <Loader size={12} color="gray" />
                      <Text size="xs">Saving…</Text>
                    </Group>
                  )}
                  {!isLocked && autoSaveStatus === 'saved' && (
                    <Group
                      gap={4}
                      align="center"
                      wrap="nowrap"
                      c="#16a34a"
                      data-testid="autosave-saved"
                    >
                      <IconCheck size={12} />
                      <Text size="xs">Saved</Text>
                    </Group>
                  )}
                </Group>
                <Group gap="xs">
                  {showAiDictate && (
                    <Button
                      variant="light"
                      color="violet"
                      size="xs"
                      leftSection={<IconMicrophone size={14} />}
                      onClick={() => dictate.start()}
                      disabled={dictate.phase !== 'idle'}
                      data-testid="progress-note-ai-dictate"
                    >
                      AI Dictate
                    </Button>
                  )}
                  {showAiMenu && (
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <Button
                          variant="light"
                          color="violet"
                          size="xs"
                          leftSection={<IconSparkles size={14} />}
                          loading={aiAction !== null}
                          data-testid="progress-note-ai-menu"
                        >
                          AI
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {aiDraftDocRefId && (
                          <Menu.Item
                            leftSection={<IconFileText size={14} />}
                            onClick={() => void handleLoadAiDraft()}
                            data-testid="progress-note-ai-load"
                          >
                            Reload Original Progress Note
                          </Menu.Item>
                        )}
                        {aiTranscriptDocRefId && (
                          <Menu.Item
                            leftSection={<IconRefresh size={14} />}
                            onClick={() => void handleRegenerateNote()}
                            data-testid="progress-note-ai-regenerate"
                          >
                            Regenerate from transcript
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                  {onCancel && (
                    <Button
                      variant="default"
                      size="xs"
                      onClick={() => {
                        void handleCancel()
                      }}
                      loading={autoSaveStatus === 'saving'}
                      disabled={dictate.phase !== 'idle'}
                      data-testid="progress-note-cancel"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="xs"
                    color="blue"
                    onClick={() => {
                      void onConfirmSubmit()
                    }}
                    disabled={!canEdit || dictate.phase !== 'idle'}
                    loading={submitting}
                    data-testid="progress-note-save"
                  >
                    Save
                  </Button>
                </Group>
              </Group>

              <AiDictateBar dictate={dictate} />

              {!canEdit && (
                <Text size="sm" c="red">
                  Provenance not found. Editing is disabled for this document.
                </Text>
              )}

              {/* Phase 4 — Stats Strip (LD-7). Renders 4 cards above the TOC.
              Hidden for non-scribe notes (manual entries) to avoid showing a
              misleading "Overdue" countdown on legacy notes that predate the
              AI scribe pilot. Also hidden in create-mode (no DocRef yet). */}
              {scribeBacked && documentReference && (
                <NoteStatsStripFormBridge
                  encounter={encounter}
                  documentReference={documentReference}
                  provenance={provenance}
                />
              )}

              {/* Docks below the sticky Save/Cancel bar above: 30px xs buttons
              + 8px padding top/bottom + 2px bottom border = 48px. */}
              <TableOfContents stickyTopPx={48} />

              <EncounterDetailsSection
                encounterDate={methods.watch('noteDate') || new Date().toISOString()}
                visitType={
                  methods.watch('visitType') === 'new' ? 'Initial Visit' : 'Follow-up Visit'
                }
                description={
                  documentReference?.description ||
                  documentReference?.type?.text ||
                  'Telehealth visit'
                }
                readOnly={!canEdit}
                readOnlyDateDisplay={encounterDateDisplay}
              />

              <SubjectiveSection readOnly={!canEdit} patientId={patientId} />

              <ObjectiveSection
                readOnly={!canEdit}
                observationsField={observationsField}
                allergiesField={allergiesField}
              />

              <AssessmentSection
                readOnly={!canEdit}
                patientId={patientId}
                conditionsField={conditionsField}
                dateOfService={visitBillingQuery.data?.dateOfService ?? undefined}
                onDiagnosisChange={handleDiagnosisChange}
              />

              {/* Billing summary is rendered as a 2-col grid at the tail of
              `<PlanSection>` (mockup parity, `<BillingSummary>`); it reads and
              writes the visit's billing itself. */}
              <PlanSection
                readOnly={!canEdit}
                docStatus={documentReference?.docStatus as 'preliminary' | 'final' | undefined}
                serviceRequestsField={serviceRequestsField}
                medicationsField={medicationsField}
                encounterId={autoSaveEncounterId}
                beforeBillingEditorOpen={openBillingEditor}
              />

              {/* PD-1321 closing order: MDM → Total Time → Follow-Up. */}
              <MdmAndTimeSection readOnly={!canEdit} />
              <FollowUpSection readOnly={!canEdit} followUpsField={followUpsField} />
              <AiDisclosureSection />
            </Stack>
          </Paper>
        </TocPresenceProvider>
      </AiDictateContext.Provider>
    </FormProvider>
  )
}
